import * as THREE from 'three';
import {
  CHAPEL_DOOR,
  CHAPEL_HALF,
  CHAPEL_N,
  CHAPEL_S,
  CISTERN_R,
  CISTERN_Y,
  CORR_HALF,
  CORR_N,
  CORR_X,
  GALLERY_HALF,
  GALLERY_Y,
  LEDGE_X,
  MON_DOOR,
  PATIO_HALF,
  PATIO_N,
  PATIO_S,
  T_PATH,
  type BattleState,
  type MapDef,
  type MonasteryRules,
  type MonasteryState,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { looksRng } from '../omens/common.ts';
import { WORLD_PER_HEX, facingToRotY } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { sendaModel } from '../models/sendas.ts';
import { SkyDome } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The monastery of the waterfall (docs/sendas/paladin.md, chapter 4), for the paladin's path: a monastery of pale stone
 * with slate roofs at the head of a deep gorge of dark wet rock, moss and ferns; behind it a tall waterfall falls into a
 * pond held by an old dam with its great wheel, and a ravine runs down its east side into the gorge. Never chapter 1's
 * black glass, chapter 2's white salt, chapter 3's rain bridge nor the warrior's snow: here it is green-black rock
 * running with water, the fall's mist, pale masonry.
 *
 * One board for three acts (sim battle/monastery-layout.ts, the same geometry for every seed):
 * - The gorge south of the front door (the Way's old paving going down it, a stream at its east foot, the stair cut in
 *   its east cliff to the «first bend»); the door in the patio's thick south wall; the patio (the refectory's hearth and
 *   salt sacks at its west end, the ledge over the ravine at its east end); the corridors A and C and the north gallery
 *   B; the cloister's arcades about its cistern (twelve marks on its inner wall, the sixth altar at its bottom, the spout
 *   the waterfall fills it by, three sluice gates and their runnels); the chapel with the fifth altar; eight cells.
 * - The walls are boxes that follow the sim's floor outline exactly (masonry, slate and moss drawn by one shader from a
 *   per-piece attribute), never hex blocks; the floor is one sheet whose shader knows the regions (paving, flags, the
 *   cloister's garth) and darkens toward the walls.
 * - Places: `gorgePast` (the memory: morning, years ago, under the canvas's grey filter), `monastery` (dusk),
 *   `monasteryNight` (the kitchen's fire), `monasteryDawn` (the test, the low point, the choice and the close).
 * - Scenes: `flood` (the dam opened: one sheet of water whose front runs down the ravine and the gorge, white at its
 *   head, the level rising over the monastery; the wheel turning alone), `torches` (the summit with Somnhold's keep, the
 *   Order's torches round it as glows).
 * - State drawn: the salt thrown (discs), the prints in it (pointing the way, fading), a door shut (a white line), the
 *   fifth altar out or burning, the cistern's level, the gates open and their water running, the sixth altar bare and
 *   lit, the young squire seen only in the water (her miniature merged pale and mirrored under the surface), the great
 *   sentinel's climb from the ravine.
 *
 * Draw calls: the terrain (one), the floor (one), the masonry, roofs, wood and plants (one merged mesh, one shader), the
 * chapel's roof (one, scenes only), the wheel (one), the waterfall (one), its mist (one), the pond, the cistern's water,
 * the runnels and the stream (one each), the decals (one instanced), the glow layer, the sky dome; the flood, the summit
 * and the reflection only when shown. No lights of its own: the board's are repainted (HOURS); fire and altars glow.
 * Measured on a phone profile at CPU ×4 (the bot playing): 60 fps, the door 69–82 ms/s of tasks, the cells 44–45, the cloister
 * 47–57; 36–39 calls (docs/RENDIMIENTO.md, «El monasterio de la cascada»). Traps: the land must leave a hole over the
 * cistern's pit; the reflection is drawn without the depth test (the pit's floor cut it). And (as the bridge's):
 * the pieces stand on `terrain.ground` and `heights` (apply() replaces both, and marks every cell as continuous ground so
 * the gorge's slope and the stair's landing are smooth), or they float or sink.
 */

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const WHITE = new THREE.Color('#ffffff');
const FIRE = new THREE.Color('#ffb070');
const CANDLE = new THREE.Color('#ffd2a0');
const TORCH = new THREE.Color('#ff9a52');

export const MONASTERY_PLACES = new Set(['gorgePast', 'monastery', 'monasteryNight', 'monasteryDawn']);

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

/* ---------- the place's sizes (world) ---------- */

const DOOR_Z = MON_DOOR * K;
const PS = PATIO_S * K;
const PN = PATIO_N * K;
const PH = PATIO_HALF * K;
const CX = CORR_X * K;
const CH = CORR_HALF * K;
const CN = CORR_N * K;
const GY = GALLERY_Y * K;
const GH = GALLERY_HALF * K;
const CHH = CHAPEL_HALF * K;
const CHN = CHAPEL_N * K;
const CHS = CHAPEL_S * K;
const CHD = CHAPEL_DOOR * K;
const CY = CISTERN_Y * K;
const CR = CISTERN_R * K;
const LX = LEDGE_X * K;
/** The corridors' inner and outer lines, the gallery's south and north lines. */
const AI = CX - CH;
const AO = CX + CH;
const GS = GY + GH;
const GN = GY - GH;
/** The cells' blocks reach this far out (|x|), the monastery's north wall this far (z). */
const CELL_OUT = 8.5;
const NORTH = -12.5;
/** Walls: the patio's and the corridors' height; the chapel's; the cells' fronts and backs. */
const WALL = 1.45;
const CHAPEL_WALL = 2.3;
const CELL_FRONT = 1.4;
const CELL_BACK = 2.35;
/** The gorge floor falls this much a world unit south of the door. */
const SLOPE = 0.07;
/** The cistern: its floor and the water at a full cap; the rim's top. */
const PIT = -1.55;
const BRIM = -0.12;
const RIM = 0.32;
/** The pond (an ellipse north of the chapel), its water; the waterfall's lip. */
const POND = { x: 1.2, z: -18.8, rx: 7.4, rz: 4.6, y: -0.4 };
const FALL = { x: 0.6, z: -23.5, top: 17.5, w0: 3.4, w1: 5.6 };
/** The ravine's line east of the monastery and its bottom. */
const ravX = (z: number): number => (z < 5 ? 12.4 + Math.sin(z * 0.23) * 0.4 : 12.4 + Math.sin(5 * 0.23) * 0.4 - (z - 5) * 0.42);
const ravY = (z: number): number => -6.2 - Math.max(0, z + 18) * 0.06;
/** The dam across the pond's east outlet, and its wheel. */
const DAM = { x: 8.6, z: -18.8, len: 5.6 };
const WHEEL = { x: 8.6, y: 2.3, z: -16.6, r: 1.25 };
/** The stair in the gorge's east cliff: from the gorge floor up to its first bend (the landing), and on up. */
const STAIR_A = { x: 3.9, z: 11.8 };
const BEND = { x: 6.6, z: 6.3, y: 3.3, r: 1.5 };
const STAIR_C = { x: 9.4, z: 7.6, y: 5.6 };
/** The summit far up the Way: Somnhold's keep (the close). */
const SUMMIT = { x: -34, z: -84 };

/** The gorge's half width at a world z (the sim's, carried on past the board). */
const gorgeW = (z: number): number => Math.max(2.4, (4400 - ((z / K - MON_DOOR) * 1400) / 10100) * K);

const smooth = (a: number, b: number, x: number): number => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const hash2 = (x: number, z: number): number => {
  const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453;
  return s - Math.floor(s);
};
const vnoise = (x: number, z: number): number => {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = x - ix;
  const fz = z - iz;
  const ux = fx * fx * (3 - 2 * fx);
  const uz = fz * fz * (3 - 2 * fz);
  const a = hash2(ix, iz);
  const b = hash2(ix + 1, iz);
  const c = hash2(ix, iz + 1);
  const d = hash2(ix + 1, iz + 1);
  return a + (b - a) * ux + (c - a) * uz + (a - b - c + d) * ux * uz;
};
const fbm = (x: number, z: number): number => vnoise(x, z) * 0.5 + vnoise(x * 2.1 + 5.2, z * 2.1 + 1.3) * 0.3 + vnoise(x * 4.3 + 9.1, z * 4.3 + 7.7) * 0.2;

/** A box's signed distance in the plane (negative inside). */
const sdBox = (x: number, z: number, x0: number, x1: number, z0: number, z1: number): number => {
  const dx = Math.max(x0 - x, x - x1);
  const dz = Math.max(z0 - z, z - z1);
  return dx > 0 && dz > 0 ? Math.hypot(dx, dz) : Math.max(dx, dz);
};

/** The gorge floor's height south of the door (the monastery's terrace is level). */
const floorY = (z: number): number => (z > DOOR_Z ? -(z - DOOR_Z) * SLOPE : 0);

/** The stair's way up the east cliff: the nearest point's distance and its height. */
const stairAt = (x: number, z: number): { d: number; y: number } => {
  // Two legs: the foot to the bend, the bend on up.
  const seg = (ax: number, az: number, ay: number, bx: number, bz: number, by: number): { d: number; y: number } => {
    const vx = bx - ax;
    const vz = bz - az;
    const t = Math.max(0, Math.min(1, ((x - ax) * vx + (z - az) * vz) / (vx * vx + vz * vz)));
    return { d: Math.hypot(x - ax - vx * t, z - az - vz * t), y: ay + (by - ay) * t };
  };
  const a = seg(STAIR_A.x, STAIR_A.z, floorY(STAIR_A.z), BEND.x, BEND.z, BEND.y);
  const b = seg(BEND.x, BEND.z, BEND.y, STAIR_C.x, STAIR_C.z, STAIR_C.y);
  const l = Math.hypot(x - BEND.x, z - BEND.z) - BEND.r;
  const best = a.d < b.d ? a : b;
  return l < best.d ? { d: Math.max(0, l), y: BEND.y } : best;
};

/**
 * The land's height at a world point: the gorge floor and the terrace level; the cliffs rising off them (the cirque
 * behind the pond the highest), ledges and strata in the noise; the pond's basin; the ravine cut deep along the east;
 * the stair's shelf.
 */
export const monLand = (x: number, z: number): number => {
  const ax = Math.abs(x);
  // The open ground: the terrace, the gorge, the way round the chapel to the pond, the pond's basin.
  const terr = sdBox(x, z, -10.2, 10.1, NORTH - 0.9, DOOR_Z + 0.25);
  const gorge = Math.max(ax - gorgeW(z), DOOR_Z - 0.1 - z);
  const north = sdBox(x, z, -7.2, 9.6, -16.2, NORTH);
  const pdx = (x - POND.x) / POND.rx;
  const pdz = (z - POND.z) / POND.rz;
  const pr = Math.sqrt(pdx * pdx + pdz * pdz);
  const pond = (pr - 1) * Math.min(POND.rx, POND.rz);
  const open = Math.min(terr, gorge, north, pond);
  const base = z > DOOR_Z ? floorY(z) : 0;
  let y: number;
  if (open <= 0) y = base;
  else {
    // Cliffs: steep off the open ground, then rolling high ground; the cirque round the pond rises the most.
    const tall = 8.5 + 9 * smooth(-14, -24, z) + 2.5 * smooth(6, 2, Math.abs(x + 9)) * smooth(-2, -10, z);
    const n = fbm(x * 0.35, z * 0.35);
    const k = smooth(0, 3.4, open);
    y = base + tall * Math.pow(k, 0.85) * (0.75 + 0.5 * n) + open * 0.18;
    // Ledges: the rise stepped in bands, a little.
    y += (fbm(x * 1.4 + 3, z * 1.4) - 0.5) * 1.1 * k;
  }
  // The pond's basin under its water.
  if (pr < 1) y = Math.min(y, POND.y - 0.25 - (1 - pr) * 0.8);
  // The ravine down the east side (from under the dam, south, then bending into the gorge beyond the board).
  if (z > -19.5 && z < 40) {
    const dR = Math.abs(x - ravX(z)) - 1.1 - (fbm(z * 0.5, 2) - 0.5) * 0.6;
    const yb = ravY(z);
    const head = smooth(-19.5, -17, z);
    const cut = yb + Math.max(0, dR) * 5.5;
    y = Math.min(y, y + (cut - y) * head);
  }
  // The stair's shelf in the gorge's east cliff.
  if (x > 2.5 && x < 11 && z > 4 && z < 13.5) {
    const st = stairAt(x, z);
    if (st.d < 0.8) y = st.y;
    else if (st.d < 1.6) y = y > st.y ? Math.min(y, st.y + (st.d - 0.8) * 6) : Math.max(y, st.y - (st.d - 0.8) * 8);
  }
  return y;
};

/** The land with the far mountains the Way climbs: higher, ridged, the summit's crag north-west. */
const farLand = (x: number, z: number): number => {
  const y = monLand(x, z);
  const far = smooth(26, 60, Math.hypot(x * 0.7, z + 8));
  return far > 0 ? y * (1 - far * 0.3) + far * (10 + 14 * fbm(x * 0.05, z * 0.05) + 70 * Math.pow(smooth(55, 6, Math.hypot(x - SUMMIT.x, z - SUMMIT.z)), 1.6)) : y;
};
export const monSummitY = (): number => farLand(SUMMIT.x, SUMMIT.z);

/**
 * The cliffs' relief (the faces read smooth, one soft slope under the strata's paint): a steep face is stepped into
 * ledges and risers, each band its own height, the step's line wandering, a jag on top, so the flat-shaded facets
 * catch the light like broken rock. Only on steep ground well over the floor (`slope`, rise over run), never on the
 * floor, the ravine, the pond's basin or the stair; the pieces read the same (monGround), so nothing floats.
 */
const relief = (x: number, z: number, y: number, slope: number): number => {
  const base = floorY(z);
  const over = y - base;
  if (over < 0.3 || slope < 0.7) return y;
  let w = smooth(0.7, 1.8, slope) * smooth(0.3, 1.4, over);
  // (Not on the stair's shelf, nor its landing.)
  if (x > 2 && x < 11.5 && z > 3.5 && z < 14) w *= smooth(0.6, 2.2, stairAt(x, z).d);
  if (w <= 0) return y;
  const h = 1.5 + 0.8 * fbm(x * 0.18 + 3.1, z * 0.18);
  const off = fbm(x * 0.3 + 5, z * 0.3) * 0.8;
  const t = over / h + off;
  const band = Math.floor(t);
  // Most of each band a ledge, then its riser.
  const yt = base + (band + smooth(0.55, 0.95, t - band) - off) * h;
  return y + (yt - y) * w * 0.75 + (fbm(x * 0.9 + 11, z * 0.9) - 0.5) * 0.7 * w;
};

/** Where the pieces stand (the floor; the stair and its bend; the land elsewhere, with the cliffs' relief). */
const monGround = (x: number, z: number): number => {
  const y = monLand(x, z);
  // (On the floor, where everyone walks, one call: the slope is only read on the cliffs.)
  if (y - floorY(z) < 0.3) return y;
  const e = 0.3;
  const sx = (monLand(x + e, z) - monLand(x - e, z)) / (2 * e);
  const sz = (monLand(x, z + e) - monLand(x, z - e)) / (2 * e);
  return relief(x, z, y, Math.hypot(sx, sz));
};

/* ---------- the floor (sim's outline) ---------- */

/** The sim's floor as a distance in world units (negative on the floor): the same outline as monFloor. */
const floorSd = (x: number, z: number): number => {
  const ax = Math.abs(x);
  const gorge = Math.max(ax - gorgeW(z), DOOR_Z - z);
  const door = sdBox(ax, z, -1, 0.8 * 1000 * K, PS, DOOR_Z + 0.01);
  const patio = sdBox(ax, z, -1, PH, PN, PS);
  const ledge = sdBox(x, z, 0, LX, -0.6, 0.6);
  const corr = sdBox(ax, z, AI, AO, CN, PN + 0.01);
  const gal = sdBox(ax, z, -1, AO, GN, GS);
  const cl = Math.max(sdBox(ax, z, -1, AI + 0.01, GS - 0.01, PN + 0.01), CR - Math.hypot(x, z - CY));
  const cd = sdBox(ax, z, -1, CHD, CHS, GN + 0.01);
  const ch = sdBox(ax, z, -1, CHH, CHN, CHS + 0.01);
  return Math.min(gorge, door, patio, ledge, corr, gal, cl, cd, ch);
};

const GLSL_COMMON = /* glsl */ `
float mnH(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float mnN(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(mnH(i), mnH(i + vec2(1, 0)), u.x), mix(mnH(i + vec2(0, 1)), mnH(i + vec2(1, 1)), u.x), u.y); }
float mnF(vec2 p) { return mnN(p) * 0.5 + mnN(p * 2.07 + 5.3) * 0.3 + mnN(p * 4.3 + 1.7) * 0.2; }
float mnBox(vec2 p, vec2 a, vec2 b) { vec2 d = max(a - p, p - b); return length(max(d, 0.0)) + min(max(d.x, d.y), 0.0); }
`;

/** The floor's regions in GLSL (world x, z): the gorge 0, the patio and door 1, the corridors, gallery and chapel 2, the cloister's garth 3. */
const GLSL_FLOOR = /* glsl */ `
float mnGorgeW(float z) { return max(2.4, (4400.0 - ((z / ${K.toFixed(6)} - ${MON_DOOR.toFixed(1)}) * 1400.0) / 10100.0) * ${K.toFixed(6)}); }
// The floor's distance (negative on it), the region it is in.
vec2 mnFloor(vec2 p) {
  float ax = abs(p.x);
  vec2 q = vec2(ax, p.y);
  // (The gorge's floor runs on past the board's foot.)
  float gorge = max(ax - mnGorgeW(p.y), ${DOOR_Z.toFixed(4)} - p.y);
  float door = mnBox(q, vec2(-1.0, ${PS.toFixed(4)}), vec2(${(800 * K).toFixed(4)}, ${(DOOR_Z + 0.01).toFixed(4)}));
  float patio = mnBox(q, vec2(-1.0, ${PN.toFixed(4)}), vec2(${PH.toFixed(4)}, ${PS.toFixed(4)}));
  float ledge = mnBox(p, vec2(0.0, -0.6), vec2(${LX.toFixed(4)}, 0.6));
  float corr = mnBox(q, vec2(${AI.toFixed(4)}, ${CN.toFixed(4)}), vec2(${AO.toFixed(4)}, ${(PN + 0.01).toFixed(4)}));
  float gal = mnBox(q, vec2(-1.0, ${GN.toFixed(4)}), vec2(${AO.toFixed(4)}, ${GS.toFixed(4)}));
  float cl = max(mnBox(q, vec2(-1.0, ${(GS - 0.01).toFixed(4)}), vec2(${(AI + 0.01).toFixed(4)}, ${(PN + 0.01).toFixed(4)})), ${CR.toFixed(4)} - length(p - vec2(0.0, ${CY.toFixed(4)})));
  float cd = mnBox(q, vec2(-1.0, ${CHS.toFixed(4)}), vec2(${CHD.toFixed(4)}, ${(GN + 0.01).toFixed(4)}));
  float ch = mnBox(q, vec2(-1.0, ${CHN.toFixed(4)}), vec2(${CHH.toFixed(4)}, ${(CHS + 0.01).toFixed(4)}));
  float inner = min(min(corr, gal), min(cd, ch));
  float d = min(min(min(gorge, door), min(patio, ledge)), min(inner, cl));
  float region = d == gorge ? 0.0 : (d == door || d == patio || d == ledge) ? 1.0 : d == cl ? 3.0 : 2.0;
  return vec2(d, region);
}
`;

type FloorU = { uTime: THREE.IUniform<number>; uWet: THREE.IUniform<number>; uNight: THREE.IUniform<number>; uFlood: THREE.IUniform<number> };

/** The floor: the Way's old paving down the gorge, the patio's great flags, the inner flags, the cloister's garth; dark toward the walls. */
const floorMat = (u: FloorU): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.78, metalness: 0.02 });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vMnW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvMnW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nuniform float uWet;\nuniform float uNight;\nvarying vec3 vMnW;\n${GLSL_COMMON}\n${GLSL_FLOOR}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
vec2 mnF0 = mnFloor(vMnW.xz);
float mnD = mnF0.x;
float mnReg = mnF0.y;
float mnWetK = 0.0;
{
  vec2 p = vMnW.xz;
  vec3 c;
  float n = mnF(p * 1.3);
  if (mnReg < 0.5) {
    // The gorge: dark wet bedrock in broken slabs (cracks from the noise, not a grid), gravel and moss between, the
    // Way's old cobbles worn into it down the middle, lost in places.
    float n2 = mnF(p * 0.55 + 7.0);
    vec3 rock = mix(vec3(0.2, 0.215, 0.215), vec3(0.32, 0.33, 0.32), n) * (0.85 + 0.3 * n2);
    // Slabs of bedrock: patches of lighter and darker rock, their edges soft, gravel speckled between.
    float slab = smoothstep(0.42, 0.58, mnN(p * 0.9 + 3.0));
    rock *= 0.82 + 0.3 * slab;
    rock *= 0.9 + 0.2 * mnH(floor(p * 14.0));
    rock = mix(rock, vec3(0.2, 0.27, 0.16), (1.0 - slab) * 0.35 * smoothstep(0.4, 0.7, n2));
    float wx = p.x + sin(p.y * 0.35) * 0.55;
    float way = (1.0 - smoothstep(0.5, 0.95, abs(wx) + (mnN(p * 2.0) - 0.5) * 0.4)) * smoothstep(0.38, 0.62, mnF(p * 0.45 + 2.0));
    vec2 q = p * vec2(3.2, 2.6);
    q.x += floor(q.y) * 0.5;
    vec2 f = fract(q);
    float joint = smoothstep(0.0, 0.14, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    vec3 cob = mix(vec3(0.27, 0.28, 0.28), vec3(0.36, 0.37, 0.36), mnH(floor(q))) * (0.6 + 0.4 * joint);
    c = mix(rock, cob, way * 0.75);
    vec3 moss = mix(vec3(0.17, 0.25, 0.13), vec3(0.26, 0.36, 0.18), mnN(p * 3.0));
    float mk = smoothstep(0.58, 0.85, mnF(p * 0.9 + 9.0)) * (1.0 - way) + (1.0 - joint) * way * 0.35;
    c = mix(c, moss, clamp(mk, 0.0, 0.8));
    mnWetK = 0.7 + 0.3 * smoothstep(0.4, 0.7, mnF(p * 0.5 + 1.0));
  } else if (mnReg < 1.5) {
    // The patio and the door: great worn flags of the pale stone, laid in rows, moss in the joints.
    vec2 q = p * vec2(0.95, 1.15);
    q.x += floor(q.y) * 0.37;
    vec2 cell = floor(q);
    vec2 f = fract(q);
    float joint = smoothstep(0.0, 0.05, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    c = mix(vec3(0.5, 0.52, 0.53), vec3(0.62, 0.63, 0.63), mnH(cell)) * (0.9 + 0.12 * n);
    c = mix(vec3(0.17, 0.23, 0.15), c, joint);
    mnWetK = 0.35;
  } else if (mnReg < 2.5) {
    // The corridors, the gallery, the chapel: smaller flags, square, worn darker down the middle where they walk.
    vec2 q = p * 1.7;
    vec2 cell = floor(q);
    vec2 f = fract(q);
    float joint = smoothstep(0.0, 0.06, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    c = mix(vec3(0.47, 0.49, 0.5), vec3(0.58, 0.59, 0.6), mnH(cell)) * (0.88 + 0.14 * n);
    // The chapel's floor: a border of dark slate round its nave.
    float ax = abs(p.x);
    if (p.y < ${CHS.toFixed(4)} && ax < ${CHH.toFixed(4)}) {
      float band = step(${(CHH - 0.4).toFixed(4)}, ax) + step(p.y, ${(CHN + 0.4).toFixed(4)});
      c = mix(c, vec3(0.24, 0.26, 0.3) * (0.9 + 0.2 * mnH(cell)), clamp(band, 0.0, 1.0));
    }
    c = mix(vec3(0.2, 0.22, 0.2), c, 0.55 + 0.45 * joint);
    mnWetK = 0.2;
  } else {
    // The cloister's garth: close moss and grass about a ring of flags round the cistern, darker in the shade.
    float r = length(p - vec2(0.0, ${CY.toFixed(4)}));
    vec3 grass = mix(vec3(0.17, 0.26, 0.14), vec3(0.27, 0.37, 0.19), mnF(p * 2.2));
    grass *= 0.85 + 0.25 * mnN(p * 9.0);
    float ring = smoothstep(${(CR + 0.85).toFixed(3)}, ${(CR + 0.7).toFixed(3)}, r);
    float a = atan(p.y - ${CY.toFixed(4)}, p.x);
    vec2 q = vec2(a * 3.2, r * 3.0);
    vec2 f = fract(q);
    float joint = smoothstep(0.0, 0.08, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y)));
    vec3 flags = mix(vec3(0.5, 0.51, 0.52), vec3(0.6, 0.61, 0.61), mnH(floor(q))) * (0.7 + 0.3 * joint);
    // A cross of paths to the four arcades.
    float paths = max(1.0 - smoothstep(0.35, 0.45, abs(p.x)), 1.0 - smoothstep(0.35, 0.45, abs(p.y - ${CY.toFixed(4)})));
    vec2 pq = p * 2.4;
    vec2 pf = fract(pq);
    float pj = smoothstep(0.0, 0.08, min(min(pf.x, 1.0 - pf.x), min(pf.y, 1.0 - pf.y)));
    vec3 pathC = mix(vec3(0.46, 0.47, 0.48), vec3(0.56, 0.57, 0.57), mnH(floor(pq))) * (0.72 + 0.28 * pj);
    c = mix(grass, pathC, paths * (1.0 - ring));
    c = mix(c, flags, ring);
    mnWetK = 0.15;
  }
  c *= 0.8;
  // Dark toward the walls (the shade of the masonry, a little damp and mossy at their foot).
  float ao = smoothstep(0.0, 0.55, -mnD);
  c *= mix(0.42, 1.0, ao);
  c = mix(c, c * vec3(0.8, 1.0, 0.75), (1.0 - ao) * 0.5);
  diffuseColor.rgb = c;
  if ((mnD > 0.06 && mnReg > 0.5) || mnD > 0.7) discard;
}`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
{
  // Wet: the gorge's rock shines, puddles in the low flags.
  float pud = smoothstep(0.62, 0.78, mnF(vMnW.xz * 0.7 + 4.0));
  float wet = clamp(mnWetK * uWet + pud * uWet * 0.6, 0.0, 1.0);
  roughnessFactor = mix(roughnessFactor, 0.18, wet);
  diffuseColor.rgb *= mix(1.0, 0.72, wet * 0.6);
}`,
      );
  };
  m.customProgramCacheKey = () => 'somnhold-mon-floor';
  return m;
};

type StoneU = { uTime: THREE.IUniform<number>; uWet: THREE.IUniform<number> };

/**
 * The masonry, slate, wood and plants, one material: `aMason` 1 cuts ashlar courses in what stands (coping on top), 2
 * lays slates in rows down a roof, 3 is the land's rock (strata and wet streaks), 0 leaves the piece its own colour.
 * Moss low on the walls and on their tops, damp streaks under the copings.
 */
const stoneMat = (u: StoneU, land = false): THREE.MeshStandardMaterial => {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: land ? 0.86 : 0.8, metalness: 0.02, flatShading: true });
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${land ? '' : 'attribute float aMason;'}\nvarying float vMason;\nvarying vec3 vMnW;\nvarying vec3 vMnN;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\nvMason = ${land ? '3.0' : 'aMason'};\nvMnW = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvMnN = normalize(mat3(modelMatrix) * objectNormal);`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\nuniform float uTime;\nuniform float uWet;\nvarying float vMason;\nvarying vec3 vMnW;\nvarying vec3 vMnN;\n${GLSL_COMMON}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
float mnWet = 0.0;
float mnBH = 0.0;
{
  vec3 w = vMnW;
  vec3 nn = normalize(vMnN);
  float up = nn.y;
  float side = 1.0 - smoothstep(0.35, 0.7, abs(up));
  if (vMason > 0.5 && vMason < 1.5) {
    // Ashlar: courses along y, blocks staggered along the face (by x or z, whichever runs along it).
    float along = abs(nn.x) > abs(nn.z) ? w.z : w.x;
    float ch = 0.2;
    float row = floor(w.y / ch);
    float u = along / (0.36 + 0.2 * mnH(vec2(row, 3.0))) + row * 0.5;
    float fv = fract(w.y / ch);
    float fu = fract(u);
    float jt = smoothstep(0.0, 0.06, min(fv, 1.0 - fv)) * smoothstep(0.0, 0.04, min(fu, 1.0 - fu));
    float shade = 0.8 + 0.26 * mnH(vec2(floor(u), row));
    shade *= 0.88 + 0.2 * mnF(w.xz * 0.6 + w.y * 0.9);
    vec3 c = diffuseColor.rgb * shade;
    // Tops: coping slabs, a little lighter.
    if (up > 0.7) { vec2 q = w.xz * 2.6; vec2 f = fract(q); jt = smoothstep(0.0, 0.07, min(min(f.x, 1.0 - f.x), min(f.y, 1.0 - f.y))); c = diffuseColor.rgb * (0.82 + 0.16 * mnH(floor(q))) * (0.85 + 0.2 * mnF(w.xz * 1.3)); }
    c *= mix(0.55, 1.0, jt * side + (1.0 - side) * mix(0.75, 1.0, jt));
    // Moss and damp at the foot, on the tops; dark streaks of runoff down the faces.
    float foot = 1.0 - smoothstep(0.0, 0.55, w.y - (w.z > ${DOOR_Z.toFixed(3)} ? -(w.z - ${DOOR_Z.toFixed(3)}) * ${SLOPE} : 0.0));
    float mossN = mnF(w.xz * 1.7 + w.y * 0.6);
    float moss = clamp(foot * 0.8 + step(0.7, up) * smoothstep(0.45, 0.75, mossN) * 0.8, 0.0, 1.0) * smoothstep(0.3, 0.6, mossN + foot * 0.3);
    c = mix(c, mix(vec3(0.2, 0.28, 0.15), vec3(0.3, 0.38, 0.2), mossN), moss * 0.75);
    float streak = smoothstep(0.55, 0.9, mnN(vec2(along * 5.0, w.y * 0.4))) * side;
    c *= 1.0 - streak * 0.18;
    diffuseColor.rgb = c;
    mnWet = foot * 0.6;
  } else if (vMason > 1.5 && vMason < 2.5) {
    // Slate: rows down the slope, each slate its own grey-blue, the rows' edges catching the light; moss in patches.
    float row = floor(w.y / 0.11);
    float u = (w.x + w.z) / 0.2 + row * 0.5;
    float fv = fract(w.y / 0.11);
    float fu = fract(u);
    float jt = smoothstep(0.0, 0.12, fv) * smoothstep(0.0, 0.05, min(fu, 1.0 - fu));
    vec3 c = diffuseColor.rgb * (0.8 + 0.3 * mnH(vec2(floor(u), row)));
    c *= mix(0.6, 1.0, jt);
    c += vec3(0.05) * smoothstep(0.8, 1.0, fv);
    float moss = smoothstep(0.66, 0.86, mnF(w.xz * 2.3 + 2.0));
    c = mix(c, vec3(0.24, 0.3, 0.18), moss * 0.45);
    diffuseColor.rgb = c;
    mnWet = 0.5;
  } else if (vMason > 2.5) {
    // The land's rock: strata on the faces, moss where it lies flatter, wet black streaks where water runs.
    // Beds of rock in bands (each a shade of its own), a dark crack between beds, upright joints breaking them into
    // blocks, pale lichen on the blocks' faces; and the same as a height (mnBH) that tilts the facets' light.
    // (Beds about two units thick: at one a unit the faces read as a fine wood grain from the board's camera.)
    float n = mnF(w.xz * 0.45 + w.y * 0.2);
    float sy = w.y * 0.5 + n * 1.3;
    float bed = floor(sy);
    float fs = fract(sy);
    float strata = smoothstep(0.2, 0.8, fs);
    float along = w.x * 0.8 + w.z * 0.6;
    float block = floor(along * 0.7 + mnH(vec2(bed, 1.0)) * 3.0);
    float joint = smoothstep(0.9, 0.98, mnN(vec2(along * 0.7, bed * 3.7)));
    float crack = (1.0 - smoothstep(0.0, 0.04, fs)) * 0.7 + joint * 0.35;
    vec3 c = diffuseColor.rgb * (0.8 + 0.3 * n) * mix(0.78, 1.1, strata * side) * (0.84 + 0.3 * mnH(vec2(bed, block)) * side);
    c *= 1.0 - clamp(crack, 0.0, 1.0) * 0.32 * side;
    float lichen = smoothstep(0.72, 0.86, mnF(w.xz * 1.8 + w.y * 0.9)) * side;
    c = mix(c, vec3(0.58, 0.61, 0.58), lichen * 0.3);
    float run = smoothstep(0.6, 0.95, mnN(vec2((w.x + w.z) * 2.2, w.y * 0.25 - uTime * 0.05))) * side;
    c *= 1.0 - run * 0.35;
    diffuseColor.rgb = c;
    // (Only the runs shine: wet all over, the bumped faces glinted like oilcloth.)
    mnWet = run * 0.9 + side * 0.08;
    // Each bed bulging out to its rounded lip, lumps over it: broad, so the light tilts across a face, not in a grain.
    // (Continuous across a bed's edge, 0 at both ends: a jump there made the derivatives sparkle along every crack.)
    float bulge = smoothstep(0.0, 0.3, fs) * (1.0 - smoothstep(0.7, 1.0, fs));
    mnBH = (bulge * 0.2 + mnF(w.xz * 0.8 + w.y * 0.6) * 0.2) * side;
  } else {
    diffuseColor.rgb *= 0.92 + 0.12 * mnN(w.xz * 4.0 + w.y * 2.0);
  }
}`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
roughnessFactor = mix(roughnessFactor, 0.22, clamp(mnWet * uWet, 0.0, 1.0));`,
      )
      .replace(
        '#include <normal_fragment_maps>',
        land
          ? // The rock's relief in the light: the beds' height bends the facet's normal (screen derivatives, as a bump
            // map without its texture; no texture read, no extra pass).
            `#include <normal_fragment_maps>
{
  vec3 mnSx = dFdx(-vViewPosition);
  vec3 mnSy = dFdy(-vViewPosition);
  vec3 mnR1 = cross(mnSy, normal);
  vec3 mnR2 = cross(normal, mnSx);
  float mnDet = dot(mnSx, mnR1);
  vec3 mnG = sign(mnDet) * (dFdx(mnBH) * mnR1 + dFdy(mnBH) * mnR2) * 1.6;
  normal = normalize(abs(mnDet) * normal - mnG);
}`
          : '#include <normal_fragment_maps>',
      );
  };
  m.customProgramCacheKey = () => (land ? 'somnhold-mon-land' : 'somnhold-mon-stone');
  return m;
};

/** Water: the pond (deep green-black, the sky in it, foam where the fall comes down), the cistern (still), a runnel, the stream. */
const waterMat = (kind: 'pond' | 'cistern' | 'run'): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: {
      time: { value: 0 },
      sky: { value: new THREE.Color('#56616f') },
      deep: { value: new THREE.Color('#0e1a18') },
      night: { value: 0 },
      flow: { value: 1 },
      fall: { value: new THREE.Vector3(FALL.x, POND.y, FALL.z + 0.9) },
      glint: { value: new THREE.Color('#ffffff') },
    },
    vertexShader: /* glsl */ `
      attribute float aFlow;
      varying vec3 vW;
      varying vec2 vUv;
      varying float vFlow;
      void main() { vUv = uv; vFlow = aFlow; vec4 w = modelMatrix * vec4(position, 1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 sky;
      uniform vec3 deep;
      uniform float night;
      uniform float flow;
      uniform vec3 fall;
      uniform vec3 glint;
      varying vec3 vW;
      varying vec2 vUv;
      varying float vFlow;
      ${GLSL_COMMON}
      void main() {
        vec3 v = normalize(cameraPosition - vW);
        float fr = pow(1.0 - clamp(v.y, 0.0, 1.0), 2.2);
        ${
          kind === 'pond'
            ? `float d = length((vW.xz - fall.xz) * vec2(0.7, 1.0));
        float n = mnF(vW.xz * 1.4 + vec2(0.0, time * 0.3));
        vec3 c = mix(deep, sky, 0.18 + fr * 0.55 + n * 0.08);
        // White water at the fall's foot: a boil of fine bubbles where it lands, then thin streaks of foam running
        // out from it (polar: drawn along the way the water goes, scrolling outward), breaking up as they spread.
        // (One soft blotchy noise heaped at the foot read as patches of paint, not as foam.)
        vec2 rel = vW.xz - fall.xz;
        float ang = atan(rel.x, rel.y);
        float rings = sin(d * 6.0 - time * 3.0) * 0.5 + 0.5;
        float grain = mnN(vW.xz * 11.0 + vec2(0.0, -time * 1.7)) * 0.6 + mnN(vW.xz * 23.0 - vec2(time * 0.9, 0.0)) * 0.4;
        float boil = (1.0 - smoothstep(0.4, 1.9, d)) * smoothstep(0.25, 0.6, grain + 0.35 * (1.0 - smoothstep(0.0, 1.2, d)));
        float streak = mnN(vec2(ang * 9.0, d * 0.9 - time * 0.9)) * 0.65 + mnN(vec2(ang * 23.0, d * 2.2 - time * 1.4)) * 0.35;
        float trail = smoothstep(0.58, 0.8, streak) * smoothstep(0.6, 1.6, d) * (1.0 - smoothstep(2.4, 6.5, d));
        trail *= smoothstep(0.2, 0.55, grain);
        float foam = clamp(boil + trail * 0.8, 0.0, 1.0);
        c += vec3(0.6, 0.66, 0.7) * rings * 0.05 * (1.0 - smoothstep(1.0, 6.0, d));
        c = mix(c, vec3(0.9, 0.93, 0.95) * mix(1.0, 0.5, night), foam);
        // The shore: lighter, a lip of foam on the stones.
        float a = 0.9;`
            : kind === 'cistern'
              ? `float n = mnF(vW.xz * 2.4 + vec2(time * 0.08, 0.0));
        vec3 c = mix(deep, sky, 0.2 + fr * 0.6 + n * 0.08);
        // Rings where the spout falls in.
        float d = length(vW.xz - fall.xz);
        c += vec3(0.7, 0.76, 0.8) * (sin(d * 18.0 - time * 5.0) * 0.5 + 0.5) * (1.0 - smoothstep(0.05, 0.9, d)) * 0.18;
        c = mix(c, vec3(0.88, 0.92, 0.95), (1.0 - smoothstep(0.0, 0.25, d)) * 0.6);
        float a = 0.82;`
              : `float run = mnN(vec2(vUv.x * 4.0, vUv.y * 3.0 - time * 3.2)) * 0.6 + mnN(vec2(vUv.x * 9.0, vUv.y * 7.0 - time * 5.0)) * 0.4;
        float k = flow * vFlow;
        vec3 c = mix(deep * 1.4, sky * 1.25, 0.3 + 0.4 * run * k + fr * 0.25);
        c += vec3(0.8, 0.85, 0.9) * smoothstep(0.62, 0.95, run) * k * 0.55;
        float across = 1.0 - abs(vUv.x - 0.5) * 2.0;
        float a = smoothstep(0.0, 0.35, across) * mix(0.0, 0.9, k);`
        }
        c *= mix(1.0, 0.5, night);
        gl_FragColor = vec4(c, a);
        #include <colorspace_fragment>
      }`,
  });

/**
 * The waterfall: a tall sheet down the cirque's face, its streaks scrolling down (two layers at two speeds), thinner
 * and torn at its edges, white where it breaks; brighter where it meets the pond. Never particles.
 */
const fallMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    fog: true,
    uniforms: { ...THREE.UniformsLib.fog, time: { value: 0 }, tint: { value: new THREE.Color('#dfe8ee') }, shade: { value: new THREE.Color('#5a6a72') }, night: { value: 0 }, flood: { value: 0 } },
    vertexShader: /* glsl */ `
      #include <fog_pars_vertex>
      varying vec2 vUv;
      void main() { vUv = uv; vec4 mvPosition = modelViewMatrix * vec4(position, 1.0); gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <fog_pars_fragment>
      uniform float time;
      uniform vec3 tint;
      uniform vec3 shade;
      uniform float night;
      uniform float flood;
      varying vec2 vUv;
      ${GLSL_COMMON}
      void main() {
        // uv.x across, uv.y up (1 at the lip).
        float x = vUv.x;
        float y = vUv.y;
        float sp = 1.0 + flood * 0.8;
        float s1 = mnN(vec2(x * 26.0, y * 3.0 + time * 1.9 * sp));
        float s2 = mnN(vec2(x * 61.0 + 3.0, y * 7.0 + time * 3.1 * sp));
        float s3 = mnF(vec2(x * 9.0, y * 1.4 + time * 0.9 * sp));
        float streak = s1 * 0.5 + s2 * 0.35 + s3 * 0.35;
        // The edges torn: thin and gappy at both sides; wider and whiter where it breaks at the foot.
        float edge = smoothstep(0.0, 0.16 + 0.1 * s3, x) * smoothstep(0.0, 0.16 + 0.1 * s3, 1.0 - x);
        float body = mix(0.5, 1.0, flood) + 0.5 * smoothstep(0.35, 0.8, streak);
        float foot = 1.0 - smoothstep(0.0, 0.2, y);
        vec3 c = mix(shade, tint, smoothstep(0.25, 0.85, streak) * 0.8 + 0.2 + foot * 0.5);
        c = mix(c, c * 0.55, night * 0.6);
        float a = edge * clamp(body * 0.78 + foot * 0.3, 0.0, 0.96);
        // The lip: a smooth glassy band where it pours over.
        a *= 0.55 + 0.45 * smoothstep(1.0, 0.94, y);
        gl_FragColor = vec4(c, a);
        #include <colorspace_fragment>
        #include <fog_fragment>
      }`,
  });

/** The mist off the fall's foot (and the flood's front): soft rolling noise on upright sheets, fading at their rims. */
const mistMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { time: { value: 0 }, tint: { value: new THREE.Color('#e4ecf0') }, strength: { value: 1 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform vec3 tint;
      uniform float strength;
      varying vec2 vUv;
      ${GLSL_COMMON}
      void main() {
        vec2 p = vUv;
        float n = mnF(vec2(p.x * 3.0 + time * 0.12, p.y * 2.0 - time * 0.4)) * 0.7 + mnF(vec2(p.x * 7.0 - time * 0.2, p.y * 5.0 - time * 0.9)) * 0.3;
        float shape = smoothstep(0.0, 0.3, p.x) * smoothstep(0.0, 0.3, 1.0 - p.x) * smoothstep(1.0, 0.25, p.y) * smoothstep(0.0, 0.08, p.y);
        float a = shape * smoothstep(0.3, 0.75, n) * 0.55 * strength;
        gl_FragColor = vec4(tint, a);
        #include <colorspace_fragment>
      }`,
  });

/**
 * The flood (the low point): one sheet over the gorge, the ravine and the monastery. Its front runs south from the dam
 * (`front`, world z), a white heaped wall at its head, streaks rushing south behind it; its level rises (`level`).
 */
const floodMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    uniforms: { time: { value: 0 }, front: { value: -30 }, level: { value: 0 }, sky: { value: new THREE.Color('#8994a2') } },
    vertexShader: /* glsl */ `
      uniform float front;
      uniform float level;
      varying vec3 vW;
      void main() {
        vec4 w = modelMatrix * vec4(position, 1.0);
        float behind = smoothstep(front + 0.5, front - 3.0, w.z);
        // The head heaps up white over the front; behind it, the level; ahead of it, nothing (sunk under the land).
        float head = exp(-pow((w.z - front) * 0.55, 2.0)) * 1.6;
        w.y = mix(-14.0, level + w.y, behind) + head * behind;
        vW = w.xyz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float front;
      uniform vec3 sky;
      varying vec3 vW;
      ${GLSL_COMMON}
      void main() {
        float rush = mnF(vec2(vW.x * 1.3, vW.z * 0.35 - time * 4.0)) * 0.6 + mnF(vec2(vW.x * 3.1, vW.z * 0.9 - time * 6.5)) * 0.4;
        float near = 1.0 - smoothstep(0.0, 9.0, front - vW.z);
        vec3 c = mix(vec3(0.2, 0.26, 0.29), sky * 0.7, 0.2 + 0.35 * rush);
        // Torn white over the brown-grey rush, white heaped at the front.
        c = mix(c, vec3(0.93, 0.96, 0.98), clamp(smoothstep(0.55, 0.85, rush) * 0.85 + near * 0.9, 0.0, 1.0));
        float a = 0.94;
        gl_FragColor = vec4(c, a);
        #include <colorspace_fragment>
      }`,
  });

/** The young squire in the water: pale, rippling, fading at the rim, seen only from above it. */
const reflectMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    depthTest: false,
    side: THREE.DoubleSide,
    uniforms: { time: { value: 0 }, show: { value: 0 }, surf: { value: 0 } },
    vertexShader: /* glsl */ `
      attribute vec3 color;
      uniform float time;
      varying vec3 vC;
      varying vec3 vW;
      void main() {
        vC = color;
        vec4 w = modelMatrix * vec4(position, 1.0);
        w.x += sin(w.y * 9.0 + time * 2.2) * 0.025;
        vW = w.xyz;
        gl_Position = projectionMatrix * viewMatrix * w;
      }`,
    fragmentShader: /* glsl */ `
      uniform float time;
      uniform float show;
      uniform float surf;
      varying vec3 vC;
      varying vec3 vW;
      void main() {
        // Pale through and through (the fog's squire, as chapter 2 saw her): her shapes only by their shade.
        float l = dot(vC, vec3(0.3, 0.55, 0.15));
        vec3 c = vec3(0.8, 0.86, 0.93) * (0.72 + 0.4 * l);
        float depth = clamp((surf - vW.y) / 1.2, 0.0, 1.0);
        float a = show * (0.7 - depth * 0.3) * (0.82 + 0.18 * sin(vW.y * 30.0 + time * 3.0));
        gl_FragColor = vec4(c, a);
        #include <colorspace_fragment>
      }`,
  });

const HOURS: Record<
  'gorgePast' | 'monastery' | 'monasteryNight' | 'monasteryDawn',
  { bg: string; fog: string; near: number; far: number; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sun: [number, number, number]; water: [string, string]; wet: number; night: number; fall: [string, string]; mist: string }
> = {
  // The memory: a clear morning, the sun low over the east rim, the gorge in cool shade and the mist bright.
  gorgePast: { bg: '#a9bccb', fog: '#a6b7c3', near: 56, far: 170, hemi: ['#dbe7f4', '#1c221f', 1.2], key: ['#fff4e6', 2.7], rim: ['#b4d0ff', 1.0], sky: ['#5f8fbf', '#cad9e4', '#fff6e8'], sun: [0.6, 0.45, -0.66], water: ['#9fb6c4', '#0f201d'], wet: 0.7, night: 0, fall: ['#f4f8fa', '#7f939c'], mist: '#f2f6f8' },
  // Dusk: the rim lit rose-grey, the gorge in blue shadow.
  monastery: { bg: '#4e5873', fog: '#5a6278', near: 40, far: 140, hemi: ['#a9b5d4', '#1a1c22', 1.7], key: ['#f2d6d0', 1.35], rim: ['#7f98d6', 1.25], sky: ['#1d2540', '#8a7c8e', '#f0b8a8'], sun: [-0.7, 0.18, -0.6], water: ['#5c6580', '#0b1414'], wet: 0.85, night: 0.15, fall: ['#d6dceb', '#4c586a'], mist: '#c6cede' },
  // Night: the moon over the fall, the kitchen's fire the warmth.
  monasteryNight: { bg: '#090c14', fog: '#0e131d', near: 34, far: 110, hemi: ['#6a7cab', '#07080c', 1.0], key: ['#a9bbe8', 0.78], rim: ['#6f8cf2', 1.1], sky: ['#03050a', '#141c2c', '#cfdcff'], sun: [0.3, 0.6, -0.75], water: ['#26324a', '#05090a'], wet: 0.9, night: 1, fall: ['#9aa8c8', '#2a3348'], mist: '#7d8aa8' },
  // Dawn after the night of the siege: a cold pale light, pink low in the east, the stone very pale.
  monasteryDawn: { bg: '#8a94a8', fog: '#8f98aa', near: 42, far: 140, hemi: ['#c6d0e2', '#22252b', 1.85], key: ['#ffe6e2', 1.5], rim: ['#9ab2e6', 1.2], sky: ['#3d5274', '#b9b8c6', '#ffd8cc'], sun: [0.75, 0.22, -0.5], water: ['#8b98ac', '#0e1816'], wet: 0.8, night: 0, fall: ['#eef3f8', '#6c7a88'], mist: '#e6ebf2' },
};
type Place = keyof typeof HOURS;
const HOUR_PLACE: Record<Hour, Place> = { day: 'gorgePast', dusk: 'monastery', night: 'monasteryNight', dawn: 'monasteryDawn' };

/** The decals' kinds (their quad drawn by the shader). */
const D_SALT = 0;
const D_PRINT = 1;
const D_LINE = 2;
const D_RING = 3;
const D_SOFT = 4;

/** Colours of the masonry and its kin. */
const STONE = ['#8f9397', '#999da1', '#878b90', '#a3a6a9'];
const STONE_D = '#767b80';
const SLATE = ['#3b3f46', '#43474e', '#363a41'];
const WOOD = '#4a3c33';
const WOOD_D = '#2f2722';
const IRON = '#2a2e35';

/** Kit's per-piece attribute: what the shader does with it (see stoneMat). */
const M_PLAIN = { aMason: 0 };
const M_ASHLAR = { aMason: 1 };
const M_SLATE = { aMason: 2 };

export class MonasteryLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow = new GlowLayer(160);
  private dome: SkyDome | null = null;
  private fu: FloorU = { uTime: { value: 0 }, uWet: { value: 0.8 }, uNight: { value: 0 }, uFlood: { value: 0 } };
  private su: StoneU = { uTime: { value: 0 }, uWet: { value: 0.8 } };
  private hour: Place = 'monastery';
  private now = 0;
  private lastTick = -1;
  private low: boolean;
  private kind: MonasteryRules['kind'] | null;
  // The things that move or change.
  private chapelRoof: THREE.Mesh | null = null;
  private wheel: THREE.Mesh | null = null;
  private wheelSpin = 0;
  private fall: THREE.ShaderMaterial | null = null;
  private mist: THREE.ShaderMaterial | null = null;
  private waters: THREE.ShaderMaterial[] = [];
  private pond: THREE.ShaderMaterial | null = null;
  private cistern: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; shown: number } | null = null;
  private runnels: { mat: THREE.ShaderMaterial; flow: number; gate: THREE.Mesh; up: number }[] = [];
  private decals: THREE.InstancedMesh | null = null;
  private altar5: { at: THREE.Vector3; mat: THREE.MeshStandardMaterial; open: number } | null = null;
  private altar6: { at: THREE.Vector3; mat: THREE.MeshStandardMaterial; open: number } | null = null;
  private candles: THREE.Vector3[] = [];
  private lamps: THREE.Vector3[] = [];
  private hearth = new THREE.Vector3(-6.0, 0.15, 0);
  /** A scene (the view draws a scene's campfire itself; a fight at night has the kitchen's embers). */
  private scene = false;
  private flood: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; t0: number } | null = null;
  private floodOn = false;
  private summit: THREE.Mesh | null = null;
  private torchesOn = false;
  private torchAt: THREE.Vector3[] = [];
  private reflection: { mesh: THREE.Mesh; mat: THREE.ShaderMaterial; show: number } | null = null;
  private steam: { at: THREE.Vector3; until: number }[] = [];
  /** The rise of the great sentinel: dust at the ledge (until, s). */
  private riseUntil = 0;
  private base: { hemi: number; key: number } = { hemi: 1, key: 1 };
  readonly anchors: {
    spots: THREE.Vector3[];
    doors: THREE.Vector3[];
    sack: THREE.Vector3 | null;
    gates: THREE.Vector3[];
    altar5: THREE.Vector3 | null;
    altar6: THREE.Vector3 | null;
    cistern: THREE.Vector3 | null;
    door: THREE.Vector3 | null;
  } = { spots: [], doors: [], sack: null, gates: [], altar5: null, altar6: null, cistern: null, door: null };

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `monastery-${place}`;
    this.low = ctx.low;
    const r = s.rules?.senda?.monastery;
    this.kind = r?.kind ?? null;
    this.hour = (MONASTERY_PLACES.has(place) ? place : 'monastery') as Place;
    const kit = new Kit({ aMason: 0 });
    this.buildWalls(kit);
    this.buildCells(kit);
    this.buildChapel(kit);
    this.buildCloister(kit);
    this.buildPatio(kit);
    this.buildGorge(kit);
    this.buildNorth(kit);
    this.buildPlants(kit);
    const stone = stoneMat(this.su);
    this.mats.push(stone);
    const mesh = kit.build(stone, 'mon-stone');
    if (mesh) {
      this.geos.push(mesh.geometry);
      this.group.add(mesh);
    }
    this.buildLand();
    this.buildFloor();
    this.buildWater();
    this.buildFall();
    this.buildDecals();
    this.buildAnchors(r);
    // (Its glows reach the summit far off: never culled by the bounds of where it was first filled.)
    this.glow.mesh.frustumCulled = false;
    this.group.add(this.glow.mesh);
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    // The place is its own: the board's tiles and the world beyond hidden; the pieces stand on its ground.
    terrain.mesh.visible = false;
    terrain.decor.visible = false;
    if (terrain.fog) terrain.fog.group.visible = false;
    terrain.ground = (x, z) => monGround(x, z);
    const R = this.map.radius;
    const W = 2 * R + 1;
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        const id = (r + R) * W + (q + R);
        if (id < 0 || id >= terrain.heights.length) continue;
        const x = (q * 1000 + r * 500) * K;
        const z = r * 866 * K;
        terrain.heights[id] = monGround(x, z);
        // Continuous ground (the gorge's slope, the stair): every cell reads the land where the piece stands.
        terrain.terrain[id] = T_PATH;
      }
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint(this.hour);
  }

  /** Repaints the board's light for a place's hour (a scene may ask for its own). */
  paint(hour: Hour | Place): void {
    const p: Place = hour in HOURS ? (hour as Place) : HOUR_PLACE[hour as Hour];
    this.hour = p;
    const look = HOURS[p];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog);
      scene.fog.near = look.near;
      scene.fog.far = look.far;
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.base = { hemi: look.hemi[2], key: look.key[1] };
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(look.sun[0], look.sun[1], look.sun[2]).normalize();
    this.fu.uWet.value = look.wet;
    this.fu.uNight.value = look.night;
    this.su.uWet.value = look.wet;
    for (const m of this.waters) {
      (m.uniforms.sky as THREE.IUniform<THREE.Color>).value.set(look.water[0]);
      (m.uniforms.deep as THREE.IUniform<THREE.Color>).value.set(look.water[1]);
      (m.uniforms.night as THREE.IUniform<number>).value = look.night;
    }
    if (this.fall) {
      (this.fall.uniforms.tint as THREE.IUniform<THREE.Color>).value.set(look.fall[0]);
      (this.fall.uniforms.shade as THREE.IUniform<THREE.Color>).value.set(look.fall[1]);
      (this.fall.uniforms.night as THREE.IUniform<number>).value = look.night;
    }
    if (this.mist) (this.mist.uniforms.tint as THREE.IUniform<THREE.Color>).value.set(look.mist);
    if (this.flood) (this.flood.mat.uniforms.sky as THREE.IUniform<THREE.Color>).value.set(look.water[0]);
  }

  /** A scene's own: the dam opened (the flood), the summit's torches; the chapel's roof on (scenes) or off (fights). */
  cue(o: { flood?: boolean; torches?: boolean; scene?: boolean }): void {
    if (o.scene !== undefined) {
      this.scene = o.scene;
      if (this.chapelRoof) this.chapelRoof.visible = o.scene;
    }
    if (o.flood && !this.floodOn) {
      this.floodOn = true;
      this.buildFlood();
    }
    if (o.torches && !this.torchesOn) {
      this.torchesOn = true;
      this.buildSummit();
    }
  }

  /* ---------- the place ---------- */

  /** A box of masonry from x0..x1, z0..z1, its foot at y0 and top at y1 (`m`: what the shader cuts in it). */
  private box(kit: Kit, x0: number, x1: number, z0: number, z1: number, y0: number, y1: number, color: string, m: Record<string, number> = M_ASHLAR): void {
    const g = new THREE.BoxGeometry(1, 1, 1);
    kit.add(g, color, place((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2, 0, new THREE.Vector3(x1 - x0, y1 - y0, z1 - z0)), undefined, m);
    g.dispose();
  }

  /**
   * The walls along the sim's outline: the patio's south wall and its gatehouse over the door, the patio's ends, the
   * blocks between the corridors and the chapel, the corridors' north ends; coping on each.
   */
  private buildWalls(kit: Kit): void {
    const D = 0.8 * 1000 * K;
    const cope = (x0: number, x1: number, z0: number, z1: number, y: number): void => this.box(kit, x0 - 0.06, x1 + 0.06, z0 - 0.06, z1 + 0.06, y, y + 0.1, STONE[3] as string);
    // The patio's south wall, the door through it (a gatehouse: two square piers, the arch's head, a slate roof).
    for (const s of [-1, 1]) {
      const x0 = s < 0 ? -PH - 1.6 : D;
      const x1 = s < 0 ? -D : PH + 0.7;
      this.box(kit, x0, x1, PS, DOOR_Z, -0.6, WALL, STONE[s < 0 ? 0 : 2] as string);
      cope(x0, x1, PS, DOOR_Z, WALL);
      // Buttresses on its outer face, down to the gorge.
      for (let x = s * 2.4; Math.abs(x) < PH; x += s * 2.2) this.box(kit, x - 0.25, x + 0.25, DOOR_Z, DOOR_Z + 0.42, -0.4, WALL - 0.35, STONE_D, M_ASHLAR);
    }
    // The gatehouse: piers and the arch over the passage (above head height), its slate roof.
    for (const s of [-1, 1]) {
      this.box(kit, s < 0 ? -D - 0.55 : D, s < 0 ? -D : D + 0.55, PS + 0.25, DOOR_Z + 0.3, -0.5, 2.05, STONE[1] as string);
      const capG = new THREE.ConeGeometry(0.42, 0.45, 4);
      kit.add(capG, SLATE[0] as string, place(s * (D + 0.275), 2.27, (PS + DOOR_Z) / 2 + 0.28, Math.PI / 4), undefined, M_PLAIN);
      capG.dispose();
    }
    // The arch's head over the passage (its outer half: the bar inside stays in view).
    this.box(kit, -D - 0.55, D + 0.55, (PS + DOOR_Z) / 2 + 0.1, DOOR_Z + 0.3, 1.62, 2.0, STONE[3] as string);
    for (let i = 0; i < 4; i++) this.box(kit, -D - 0.4 + i * 0.62, -D - 0.1 + i * 0.62, DOOR_Z + 0.05, DOOR_Z + 0.3, 2.0, 2.2, STONE[3] as string);
    // The arch's voussoirs on the outer face.
    const vous = new THREE.BoxGeometry(0.16, 0.26, 0.12);
    for (let i = 0; i <= 10; i++) {
      const a = (i / 10) * Math.PI;
      kit.add(vous, i % 2 ? '#c6c9cc' : '#b7bbbf', place(Math.cos(a) * (D + 0.02), 1.08 + Math.sin(a) * 0.62, DOOR_Z + 0.33, 0, 1, 0, a - Math.PI / 2), undefined, M_PLAIN);
    }
    vous.dispose();
    // The door's two leaves, shut, iron-banded (the bar across them inside).
    for (const s of [-1, 1]) {
      this.box(kit, s < 0 ? -D : 0.02, s < 0 ? -0.02 : D, PS + 0.55, PS + 0.68, -0.05, 1.62, WOOD, M_PLAIN);
      for (const y of [0.35, 0.9, 1.4]) this.box(kit, s < 0 ? -D : 0.02, s < 0 ? -0.02 : D, PS + 0.68, PS + 0.71, y, y + 0.06, IRON, M_PLAIN);
    }
    this.box(kit, -D - 0.1, D + 0.1, PS + 0.42, PS + 0.54, 0.75, 0.88, WOOD_D, M_PLAIN);
    // The patio's west end: the refectory's wall (its hearth and door), and the east end's wall to the ledge.
    this.box(kit, -PH - 1.6, -PH, PN - 0.2, DOOR_Z, -0.2, 2.2, STONE[1] as string);
    cope(-PH - 1.6, -PH, PN - 0.2, DOOR_Z, 2.2);
    for (const s of [-1, 1]) {
      const z0 = s < 0 ? PN - 0.2 : 0.6;
      const z1 = s < 0 ? -0.6 : PS;
      this.box(kit, PH, PH + 0.7, z0, z1, -0.6, WALL, STONE[2] as string);
      cope(PH, PH + 0.7, z0, z1, WALL);
    }
    // The ledge's broken parapet over the ravine (a gap where the great sentinel comes up).
    for (const z of [-0.5, 0.42]) this.box(kit, LX - 0.05, LX + 0.25, z - 0.08, z + 0.12, -0.3, 0.45 + hash2(z, 3) * 0.2, STONE_D);
    // Between the corridors and the chapel; the chapel's south wall either side of its door; the corridors' north ends.
    for (const s of [-1, 1]) {
      const a = s < 0 ? -AI : CHH;
      const b = s < 0 ? -CHH : AI;
      this.box(kit, a, b, NORTH, GN, -0.2, CHAPEL_WALL - 0.55, STONE[0] as string);
      // Their lean-to roofs, low over the corridor, up against the chapel's wall.
      const g = new THREE.BoxGeometry(1, 1, 1);
      const w = Math.abs(b - a) + 0.25;
      const tilt = Math.atan2(0.62, w);
      kit.add(g, SLATE[2] as string, new THREE.Matrix4().compose(new THREE.Vector3((a + b) / 2 - s * 0.06, CHAPEL_WALL - 0.25, (NORTH + GN) / 2), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, -s * tilt)), new THREE.Vector3(Math.hypot(w, 0.62) + 0.1, 0.08, GN - NORTH + 0.2)), undefined, M_SLATE);
      g.dispose();
      // The chapel's side walls standing over them.
      this.box(kit, s < 0 ? -CHH - 0.32 : CHH, s < 0 ? -CHH : CHH + 0.32, NORTH, CHS, -0.2, CHAPEL_WALL, STONE[1] as string);
      cope(s < 0 ? -CHH - 0.32 : CHH, s < 0 ? -CHH : CHH + 0.32, NORTH, CHS, CHAPEL_WALL);
      const c0 = s < 0 ? -CHH : CHD;
      const c1 = s < 0 ? -CHD : CHH;
      this.box(kit, c0, c1, CHS, GN, -0.2, CHAPEL_WALL, STONE[1] as string);
      this.box(kit, s < 0 ? -AO : AI, s < 0 ? -AI : AO, NORTH, CN, -0.2, WALL + 0.4, STONE[2] as string);
      cope(s < 0 ? -AO : AI, s < 0 ? -AI : AO, NORTH, CN, WALL + 0.4);
    }
    // The chapel's door: a deep arch over it in the gallery's wall.
    this.box(kit, -CHD - 0.2, CHD + 0.2, CHS - 0.05, GN + 0.05, 1.7, CHAPEL_WALL + 0.15, STONE[3] as string);
    // The north wall behind the chapel.
    this.box(kit, -CHH - 0.4, CHH + 0.4, NORTH, CHN, -0.2, CHAPEL_WALL, STONE[1] as string);
  }

  /** A pitched slate roof over a span (centred x, z; width along x; depth along z; its eaves at y, its ridge `rise` above), ridge along `axis` (0: z, 1: x). */
  private gable(kit: Kit, x: number, z: number, w: number, d: number, y: number, rise: number, axis: number): void {
    const half = axis === 0 ? w / 2 : d / 2;
    const len = axis === 0 ? d : w;
    const slope = Math.hypot(half, rise);
    const tilt = Math.atan2(rise, half);
    const g = new THREE.BoxGeometry(1, 1, 1);
    for (const s of [-1, 1]) {
      const m = new THREE.Matrix4().compose(
        axis === 0 ? new THREE.Vector3(x + (s * half) / 2, y + rise / 2, z) : new THREE.Vector3(x, y + rise / 2, z + (s * half) / 2),
        new THREE.Quaternion().setFromEuler(axis === 0 ? new THREE.Euler(0, 0, -s * tilt) : new THREE.Euler(s * tilt, 0, 0)),
        axis === 0 ? new THREE.Vector3(slope + 0.2, 0.08, len + 0.2) : new THREE.Vector3(len + 0.2, 0.08, slope + 0.2),
      );
      kit.add(g, SLATE[s < 0 ? 0 : 1] as string, m, undefined, M_SLATE);
    }
    // The ridge.
    kit.add(g, '#2a303a', place(x, y + rise + 0.03, z, 0, axis === 0 ? new THREE.Vector3(0.12, 0.08, len + 0.24) : new THREE.Vector3(len + 0.24, 0.08, 0.12)), undefined, M_PLAIN);
    g.dispose();
  }

  /** The eight cells along the corridors' outer sides: a long block each side, low at the corridor, high at the back, slate pent roofs, doors. */
  private buildCells(kit: Kit): void {
    const cells = [-9840, -8040, -4080, -2160].map((z) => z / 1000);
    const g = new THREE.BoxGeometry(1, 1, 1);
    for (const s of [-1, 1]) {
      const x0 = s * AO;
      const x1 = s * CELL_OUT;
      const lo = Math.min(x0, x1);
      const hi = Math.max(x0, x1);
      // The block's walls: the front (the corridor's side) and the back; the ends; the cross walls between the cells.
      this.box(kit, lo, hi, NORTH, PN, -0.2, CELL_FRONT, STONE[s < 0 ? 1 : 0] as string);
      this.box(kit, s < 0 ? -CELL_OUT : CELL_OUT - 0.6, s < 0 ? -CELL_OUT + 0.6 : CELL_OUT, NORTH, PN, -0.2, CELL_BACK, STONE[2] as string);
      // The pent roof: low over the corridor, high at the back.
      const w = hi - lo + 0.3;
      const rise = CELL_BACK - CELL_FRONT;
      const tilt = Math.atan2(rise, w);
      const m = new THREE.Matrix4().compose(new THREE.Vector3((x0 + x1) / 2 + s * 0.12, (CELL_FRONT + CELL_BACK) / 2 + 0.05, (NORTH + PN) / 2), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, s * tilt)), new THREE.Vector3(Math.hypot(w, rise) + 0.25, 0.09, PN - NORTH + 0.25));
      kit.add(g, SLATE[s < 0 ? 2 : 1] as string, m, undefined, M_SLATE);
      // Chimneys of the cells' little hearths, in pairs.
      for (const z of [-9.0, -3.1]) this.box(kit, s * (CELL_OUT - 0.95) - 0.17, s * (CELL_OUT - 0.95) + 0.17, z - 0.17, z + 0.17, 1.6, 2.85, STONE[3] as string);
      // The doors: a dark doorway in the front with a pale lintel, a plank leaf ajar inside; a cross wall between each two.
      cells.forEach((z, i) => {
        const fx = s * AO;
        this.box(kit, fx - 0.04 * s - 0.05, fx - 0.04 * s + 0.05, z - 0.36, z + 0.36, -0.02, 1.02, '#16181b', M_PLAIN);
        this.box(kit, Math.min(fx, fx - s * 0.09), Math.max(fx, fx - s * 0.09), z - 0.48, z + 0.48, 1.02, 1.2, '#c9ccce', M_PLAIN);
        for (const k of [-1, 1]) this.box(kit, Math.min(fx, fx - s * 0.07), Math.max(fx, fx - s * 0.07), z + k * 0.43 - 0.06, z + k * 0.43 + 0.06, -0.02, 1.02, '#c3c6c9', M_PLAIN);
        const leaf = new THREE.BoxGeometry(0.06, 0.98, 0.66);
        kit.add(leaf, i % 2 ? WOOD : '#3f342c', place(fx + s * 0.32, 0.49, z + 0.28, s * 1.1), undefined, M_PLAIN);
        leaf.dispose();
        // Its worn threshold out on the corridor's floor (what reads from above: a cell's door is where its step is).
        this.box(kit, Math.min(fx, fx - s * 0.36), Math.max(fx, fx - s * 0.36), z - 0.46, z + 0.46, -0.02, 0.035, '#b9bdc1', M_PLAIN);
        // A small window high in the back wall.
        this.box(kit, s * CELL_OUT - 0.05, s * CELL_OUT + 0.05, z - 0.12, z + 0.12, 1.55, 1.85, '#121418', M_PLAIN);
      });
    }
    g.dispose();
  }

  /** The chapel: tall walls, buttresses, the north gable with its round window and bell-cote, the fifth altar; its roof (scenes only). */
  private buildChapel(kit: Kit): void {
    // Buttresses on its sides (into the blocks' tops) and the gable's end.
    for (const s of [-1, 1])
      for (const z of [-11.0, -9.6]) this.box(kit, s * CHH - 0.18 + (s > 0 ? 0 : 0), s * CHH + 0.18, z - 0.22, z + 0.22, CHAPEL_WALL - 0.2, CHAPEL_WALL + 0.55, STONE[3] as string);
    // The north gable over the chapel's end, with the bell-cote on top.
    const gab = new THREE.Shape();
    gab.moveTo(-CHH - 0.4, 0);
    gab.lineTo(CHH + 0.4, 0);
    gab.lineTo(0, 1.9);
    gab.lineTo(-CHH - 0.4, 0);
    const win = new THREE.Path();
    win.absarc(0, 0.75, 0.38, 0, Math.PI * 2, true);
    gab.holes.push(win);
    const gg = new THREE.ExtrudeGeometry(gab, { depth: 0.55, bevelEnabled: false, curveSegments: 10 });
    kit.add(gg, STONE[1] as string, place(0, CHAPEL_WALL, NORTH + 0.05), undefined, M_ASHLAR);
    gg.dispose();
    // The round window's tracery and its dark glass.
    const ringG = new THREE.TorusGeometry(0.38, 0.06, 5, 20);
    kit.add(ringG, '#c9ccce', place(0, CHAPEL_WALL + 0.75, NORTH + 0.62), undefined, M_PLAIN);
    ringG.dispose();
    const glass = new THREE.CircleGeometry(0.36, 16);
    kit.add(glass, '#1a2230', place(0, CHAPEL_WALL + 0.75, NORTH + 0.3), undefined, M_PLAIN);
    glass.dispose();
    for (let i = 0; i < 4; i++) {
      const bar = new THREE.BoxGeometry(0.04, 0.72, 0.05);
      kit.add(bar, '#c9ccce', place(0, CHAPEL_WALL + 0.75, NORTH + 0.62, 0, 1, 0, (i * Math.PI) / 4), undefined, M_PLAIN);
      bar.dispose();
    }
    // The bell-cote and its bell.
    this.box(kit, -0.36, -0.22, NORTH + 0.1, NORTH + 0.45, CHAPEL_WALL + 1.55, CHAPEL_WALL + 2.45, STONE[3] as string);
    this.box(kit, 0.22, 0.36, NORTH + 0.1, NORTH + 0.45, CHAPEL_WALL + 1.55, CHAPEL_WALL + 2.45, STONE[3] as string);
    this.box(kit, -0.42, 0.42, NORTH + 0.05, NORTH + 0.5, CHAPEL_WALL + 2.45, CHAPEL_WALL + 2.6, STONE[1] as string);
    const bell = new THREE.CylinderGeometry(0.08, 0.17, 0.26, 10);
    kit.add(bell, '#3d4148', place(0, CHAPEL_WALL + 2.1, NORTH + 0.28), undefined, M_PLAIN);
    bell.dispose();
    // The fifth altar: pale stone on its step at the chapel's end; candles either side.
    const at = new THREE.Vector3(0, 0, -8700 * K);
    this.box(kit, -0.75, 0.75, at.z - 0.55, at.z + 0.45, 0, 0.12, '#8f949b', M_PLAIN);
    this.altar5 = this.altar(kit, new THREE.Vector3(0, 0.12, at.z - 0.15), 0, 0.82);
    for (const k of [-1, 1]) {
      const st = new THREE.CylinderGeometry(0.035, 0.05, 0.62, 6);
      kit.add(st, IRON, place(k * 0.58, 0.43, at.z), undefined, M_PLAIN);
      st.dispose();
      const c = new THREE.CylinderGeometry(0.03, 0.03, 0.12, 6);
      kit.add(c, '#e8e6e2', place(k * 0.58, 0.8, at.z), undefined, M_PLAIN);
      c.dispose();
      this.candles.push(new THREE.Vector3(k * 0.58, 0.9, at.z));
    }
    // Benches down the nave (low; nothing stands on them).
    for (const s of [-1, 1])
      for (let z = -10.9; z < -9.3; z += 0.55) this.box(kit, s * 0.55, s * 1.55, z - 0.08, z + 0.08, 0.16, 0.24, WOOD, M_PLAIN);
    // The roof (scenes): a steep slate gable over the nave, its own mesh so a fight can see inside.
    const rk = new Kit({ aMason: 0 });
    this.gable(rk, 0, (CHN + CHS) / 2 - 0.15, 2 * CHH + 0.5, CHS - NORTH + 0.2, CHAPEL_WALL, 1.85, 0);
    const mat = stoneMat(this.su);
    this.mats.push(mat);
    const roof = rk.build(mat, 'mon-chapel-roof');
    if (roof) {
      this.geos.push(roof.geometry);
      roof.visible = false;
      this.chapelRoof = roof;
      this.group.add(roof);
    }
  }

  /** An altar of the first Vigil (as the bridge's): pale stone on a plinth, its eye high on its face. */
  private altar(kit: Kit, at: THREE.Vector3, face: number, scale: number): { at: THREE.Vector3; mat: THREE.MeshStandardMaterial; open: number } {
    const plinth = new THREE.CylinderGeometry(0.42 * scale, 0.48 * scale, 0.14 * scale, 6);
    const stele = new THREE.BoxGeometry(0.5 * scale, 0.98 * scale, 0.24 * scale);
    const top = new THREE.ConeGeometry(0.36 * scale, 0.26 * scale, 4);
    kit.add(plinth, '#8a9099', place(at.x, at.y + 0.07 * scale, at.z, face + Math.PI / 6), undefined, M_PLAIN);
    kit.add(stele, '#c3c8cf', place(at.x, at.y + 0.63 * scale, at.z, face), undefined, M_PLAIN);
    kit.add(top, '#cfd3d9', place(at.x, at.y + 1.25 * scale, at.z, face + Math.PI / 4, new THREE.Vector3(1, 1, 0.48)), undefined, M_PLAIN);
    plinth.dispose();
    stele.dispose();
    top.dispose();
    const fx = Math.sin(face);
    const fz = Math.cos(face);
    const eyeAt = new THREE.Vector3(at.x + fx * 0.128 * scale, at.y + 0.86 * scale, at.z + fz * 0.128 * scale);
    const lid = new THREE.TorusGeometry(0.16 * scale, 0.028 * scale, 4, 18);
    lid.scale(1, 0.5, 1);
    kit.add(lid, '#3a3d45', place(eyeAt.x, eyeAt.y, eyeAt.z, face), undefined, M_PLAIN);
    lid.dispose();
    const mat = new THREE.MeshStandardMaterial({ color: '#1c1d22', emissive: new THREE.Color('#000000'), roughness: 0.4 });
    this.mats.push(mat);
    const geo = new THREE.CircleGeometry(0.068 * scale, 16);
    this.geos.push(geo);
    const iris = new THREE.Mesh(geo, mat);
    iris.position.set(eyeAt.x + fx * 0.006, eyeAt.y, eyeAt.z + fz * 0.006);
    iris.rotation.y = face;
    this.group.add(iris);
    return { at: eyeAt, mat, open: 0 };
  }

  /** The cloister's arcades (columns and arches round the garth), the cistern's rim, its pit and marks, the sixth altar, its spout and gates. */
  private buildCloister(kit: Kit): void {
    const col = new THREE.CylinderGeometry(0.09, 0.1, 1.15, 8);
    const cap = new THREE.BoxGeometry(0.26, 0.1, 0.26);
    const basee = new THREE.BoxGeometry(0.24, 0.1, 0.24);
    const arch = (ax: number, az: number, bx: number, bz: number): void => {
      const n = 7;
      for (let i = 0; i <= n; i++) {
        const t = i / n;
        const x = ax + (bx - ax) * t;
        const z = az + (bz - az) * t;
        const y = 1.25 + Math.sin(t * Math.PI) * 0.3;
        kit.add(cap, '#c4c7ca', place(x, y, z, Math.atan2(bx - ax, bz - az), new THREE.Vector3(0.55, 0.9, 0.7)), undefined, M_PLAIN);
      }
    };
    const run = (ax: number, az: number, bx: number, bz: number, skip: (x: number, z: number) => boolean): void => {
      const len = Math.hypot(bx - ax, bz - az);
      const n = Math.max(1, Math.round(len / 1.05));
      let px = ax;
      let pz = az;
      for (let i = 0; i <= n; i++) {
        const x = ax + ((bx - ax) * i) / n;
        const z = az + ((bz - az) * i) / n;
        if (!skip(x, z)) {
          kit.add(col, STONE[3] as string, place(x, 0.62, z), undefined, M_PLAIN);
          kit.add(cap, '#c9ccce', place(x, 1.25, z), undefined, M_PLAIN);
          kit.add(basee, '#b0b4b8', place(x, 0.05, z), undefined, M_PLAIN);
        }
        if (i > 0) arch(px, pz, x, z);
        px = x;
        pz = z;
      }
    };
    // West and east (to the corridors), north (to the gallery), south (to the patio: gaps for the paths).
    for (const s of [-1, 1]) run(s * AI, PN, s * AI, GS, () => false);
    run(-AI, GS, AI, GS, (x) => Math.abs(x) < 0.5);
    run(-AI, PN, AI, PN, (x) => Math.abs(x) < 0.5);
    // Corner piers.
    for (const sx of [-1, 1]) for (const z of [PN, GS]) this.box(kit, sx * AI - 0.17, sx * AI + 0.17, z - 0.17, z + 0.17, 0, 1.42, STONE[1] as string);
    col.dispose();
    cap.dispose();
    basee.dispose();
    // The cistern: the rim, the pit's wall down to its floor, the marks cut on the wall (the second red: the altar bare).
    const rim = new THREE.CylinderGeometry(CR + 0.22, CR + 0.28, RIM + 0.05, 32, 1, true);
    kit.add(rim, STONE[3] as string, place(0, RIM / 2 - 0.02, CY), undefined, M_ASHLAR);
    rim.dispose();
    const lip = new THREE.TorusGeometry(CR + 0.11, 0.12, 5, 36);
    kit.add(lip, '#c9ccce', place(0, RIM, CY, 0, 1, Math.PI / 2, 0), undefined, M_PLAIN);
    lip.dispose();
    const wall = new THREE.CylinderGeometry(CR, CR, RIM - PIT, 32, 1, true);
    const inv = wall.clone();
    inv.scale(-1, 1, 1);
    kit.add(inv, '#6c7279', place(0, (RIM + PIT) / 2, CY), undefined, M_ASHLAR);
    wall.dispose();
    inv.dispose();
    const bottom = new THREE.CircleGeometry(CR, 24).rotateX(-Math.PI / 2);
    kit.add(bottom, '#3a4040', place(0, PIT, CY), undefined, M_PLAIN);
    bottom.dispose();
    for (let i = 1; i <= 12; i++) {
      const y = PIT + ((BRIM - PIT) * i) / 12;
      const mark = new THREE.TorusGeometry(CR - 0.012, i === 2 ? 0.018 : 0.01, 3, 32);
      kit.add(mark, i === 2 ? '#ff4f3a' : '#2a2e33', place(0, y, CY, 0, 1, Math.PI / 2, 0), undefined, M_PLAIN);
      mark.dispose();
    }
    // The sixth altar at its bottom.
    this.altar6 = this.altar(kit, new THREE.Vector3(0, PIT, CY), 0, 0.62);
    // The spout the waterfall fills it by (a lion's head of stone on the rim, north-west), and its runnel from the gallery.
    const sp = new THREE.Vector3(Math.cos(2.4) * (CR + 0.12), RIM + 0.05, CY - Math.sin(2.4) * (CR + 0.12));
    this.box(kit, sp.x - 0.18, sp.x + 0.18, sp.z - 0.18, sp.z + 0.18, 0, RIM + 0.42, STONE[1] as string);
    const head = new THREE.SphereGeometry(0.15, 8, 6);
    kit.add(head, '#9da2a8', place(sp.x * 0.93, RIM + 0.3, CY + (sp.z - CY) * 0.93), undefined, M_PLAIN);
    head.dispose();
    this.spoutAt = new THREE.Vector3(sp.x * 0.86, RIM + 0.26, CY + (sp.z - CY) * 0.86);
    // The three gates: an iron frame on the rim facing out, its leaf (moves), a wheel on a post.
    GATE_DIRS.forEach(([dx, dz]) => {
      const gx = dx * (CR + 0.25);
      const gz = CY + dz * (CR + 0.25);
      const yaw = Math.atan2(dx, dz);
      for (const k of [-1, 1]) {
        const px = gx + Math.cos(yaw) * k * 0.3;
        const pz = gz - Math.sin(yaw) * k * 0.3;
        this.box(kit, px - 0.06, px + 0.06, pz - 0.06, pz + 0.06, 0, 0.9, IRON, M_PLAIN);
      }
      kit.add(new THREE.BoxGeometry(0.72, 0.08, 0.1), IRON, place(gx, 0.92, gz, yaw), undefined, M_PLAIN);
      const ring = new THREE.TorusGeometry(0.16, 0.025, 4, 12);
      kit.add(ring, '#3b4049', place(gx + dx * 0.45, 0.75, gz + dz * 0.45, yaw), undefined, M_PLAIN);
      ring.dispose();
      this.box(kit, gx + dx * 0.45 - 0.04, gx + dx * 0.45 + 0.04, gz + dz * 0.45 - 0.04, gz + dz * 0.45 + 0.04, 0, 0.72, IRON, M_PLAIN);
    });
  }

  private spoutAt = new THREE.Vector3();

  /** The patio: the refectory's hearth and door at its west end, its tables, the salt sacks; a well-head; the hearth's fire. */
  private buildPatio(kit: Kit): void {
    // The refectory: its door and the great hearth in the west wall, the chimney over the roof; a slate roof behind.
    const wx = -PH;
    this.box(kit, wx - 0.05, wx + 0.06, -0.75, -0.05, 0, 1.25, '#15171a', M_PLAIN);
    this.box(kit, wx, wx + 0.12, -0.85, 0.05, 1.25, 1.4, '#c9ccce', M_PLAIN);
    this.box(kit, wx, wx + 0.5, 0.25, 1.15, 0, 0.12, '#8a8f96', M_PLAIN);
    this.box(kit, wx, wx + 0.42, 0.3, 1.1, 0.12, 0.95, '#121315', M_PLAIN);
    this.box(kit, wx, wx + 0.55, 0.2, 1.2, 0.95, 1.12, STONE[3] as string, M_PLAIN);
    this.box(kit, wx - 1.1, wx - 0.4, 0.4, 0.95, 2.2, 3.4, STONE[1] as string);
    this.gable(kit, wx - 0.9, 0.5, 2.0, DOOR_Z - PN + 0.3, 2.2, 0.7, 1);
    this.hearth.set(wx + 0.25, 0.3, 0.7);
    // The salt sacks against the wall by the refectory (the sim's sack spot in front of them).
    const sack = new THREE.SphereGeometry(0.2, 8, 6);
    const rng = looksRng(41);
    const sx = -5600 * K;
    for (let i = 0; i < 9; i++) {
      const x = sx - 0.55 - rng() * 0.75;
      const z = -0.55 + rng() * 1.0;
      kit.add(sack, i % 3 ? '#d9dbdc' : '#c4c6c6', place(x, 0.16 + (i > 5 ? 0.25 : 0), z, rng() * 3, new THREE.Vector3(1.1, 0.8 + rng() * 0.3, 0.9)), undefined, M_PLAIN);
    }
    // One open, its salt heaped white.
    kit.add(sack, '#cfd1d2', place(sx - 0.15, 0.14, 0.42, 0, new THREE.Vector3(1.2, 0.7, 1.1)), undefined, M_PLAIN);
    const heap = new THREE.ConeGeometry(0.2, 0.14, 8);
    kit.add(heap, '#f4f6f7', place(sx - 0.15, 0.3, 0.42), undefined, M_PLAIN);
    heap.dispose();
    sack.dispose();
    // A trestle table and benches along the north side of the west end (out of the way).
    this.box(kit, -7.6, -5.9, -0.82, -0.52, 0.48, 0.55, WOOD, M_PLAIN);
    for (const x of [-7.4, -6.1]) this.box(kit, x - 0.04, x + 0.04, -0.8, -0.54, 0, 0.48, WOOD_D, M_PLAIN);
    // The kitchen fire in the patio's west end (night): a ring of stones, a tripod and its pot.
    const fx = -6.0;
    const fz = 0;
    const stn = new THREE.DodecahedronGeometry(0.08, 0);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      kit.add(stn, '#6d7278', place(fx + Math.cos(a) * 0.26, 0.04, fz + Math.sin(a) * 0.26, a), undefined, M_PLAIN);
    }
    stn.dispose();
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const leg = new THREE.CylinderGeometry(0.015, 0.015, 0.75, 4);
      kit.add(leg, IRON, place(fx + Math.cos(a) * 0.18, 0.34, fz + Math.sin(a) * 0.18, 0, 1, Math.sin(a) * 0.25, -Math.cos(a) * 0.25), undefined, M_PLAIN);
      leg.dispose();
    }
    const pot = new THREE.SphereGeometry(0.12, 8, 6, 0, Math.PI * 2, Math.PI * 0.3, Math.PI * 0.7);
    kit.add(pot, '#24272c', place(fx, 0.38, fz), undefined, M_PLAIN);
    pot.dispose();
    this.fireAt = new THREE.Vector3(fx, 0.12, fz);
    // Lanterns on brackets: by the door inside, at the corridors' mouths, at the chapel's door.
    const lamp = new THREE.BoxGeometry(0.13, 0.17, 0.13);
    for (const [x, z] of [
      [-1.2, PS - 0.05],
      [1.2, PS - 0.05],
      [-AO + 0.08, PN - 0.3],
      [AO - 0.08, PN - 0.3],
      [-CHD - 0.3, GN + 0.06],
      [CHD + 0.3, GN + 0.06],
    ] as const) {
      kit.add(lamp, IRON, place(x, 1.15, z), undefined, M_PLAIN);
      this.lamps.push(new THREE.Vector3(x, 1.15, z));
    }
    lamp.dispose();
  }

  private fireAt: THREE.Vector3 | null = null;

  /** The gorge: boulders at the cliffs' feet, the stair cut in the east cliff to its bend, a wayside cairn. */
  private buildGorge(kit: Kit): void {
    const rng = looksRng(77);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const n = this.low ? 26 : 46;
    for (let i = 0; i < n; i++) {
      const z = DOOR_Z + 0.6 + rng() * 15;
      const side = rng() < 0.5 ? -1 : 1;
      const x = side * (gorgeW(z) + 0.15 + rng() * 0.9);
      const s = 0.35 + rng() * 0.75;
      const y = monLand(x, z);
      const c = ['#3f4549', '#4a5054', '#363c40'][i % 3] as string;
      kit.add(rock, c, place(x, y + s * 0.15, z, rng() * 6, new THREE.Vector3(s * (0.9 + rng() * 0.6), s * (0.6 + rng() * 0.5), s * (0.9 + rng() * 0.5)), rng() * 0.4, rng() * 0.4), undefined, { aMason: 3 });
    }
    // Rocks heaped along the cliffs of the terrace and the pond.
    for (let i = 0; i < (this.low ? 14 : 26); i++) {
      const a = rng();
      const x = a < 0.5 ? -10.4 - rng() * 1.2 : 10.0 + rng() * 0.4;
      const z = -12 + rng() * 13;
      const s = 0.4 + rng() * 0.6;
      kit.add(rock, '#40464a', place(x, monLand(x, z) + s * 0.1, z, rng() * 6, new THREE.Vector3(s, s * 0.7, s)), undefined, { aMason: 3 });
    }
    rock.dispose();
    // The stair: steps cut along the shelf from the gorge floor to the bend, a low wall on the drop's side.
    const step = new THREE.BoxGeometry(1, 1, 1);
    const legs: [number, number, number, number, number, number, number][] = [
      [STAIR_A.x, STAIR_A.z, floorY(STAIR_A.z), BEND.x, BEND.z, BEND.y, 18],
      [BEND.x, BEND.z, BEND.y, STAIR_C.x, STAIR_C.z, STAIR_C.y, 9],
    ];
    for (const [ax, az, ay, bx, bz, by, n2] of legs) {
      const yaw = Math.atan2(bx - ax, bz - az);
      const len = Math.hypot(bx - ax, bz - az);
      for (let i = 0; i < n2; i++) {
        const t = (i + 0.5) / n2;
        const x = ax + (bx - ax) * t;
        const z = az + (bz - az) * t;
        const y = ay + (by - ay) * ((i + 1) / n2);
        kit.add(step, i % 2 ? '#7e8389' : '#888d93', place(x, y - 0.25, z, yaw, new THREE.Vector3(1.05, 0.5, len / n2 + 0.03)), undefined, M_PLAIN);
      }
    }
    step.dispose();
    // The bend's landing: a flat of old flags, a low parapet over the gorge, a wayside stone with the Vigil's eye.
    const disc = new THREE.CylinderGeometry(BEND.r, BEND.r + 0.1, 0.3, 14);
    kit.add(disc, '#80868c', place(BEND.x, BEND.y - 0.15, BEND.z), undefined, M_PLAIN);
    disc.dispose();
    for (let i = 0; i < 6; i++) {
      const a = Math.PI * 0.75 + (i / 5) * Math.PI * 0.95;
      const x = BEND.x + Math.cos(a) * (BEND.r + 0.05);
      const z = BEND.z + Math.sin(a) * (BEND.r + 0.05);
      this.box(kit, x - 0.16, x + 0.16, z - 0.16, z + 0.16, BEND.y - 0.1, BEND.y + 0.35 + (i % 2) * 0.06, STONE_D);
    }
    this.box(kit, BEND.x + 0.7, BEND.x + 0.95, BEND.z - 0.75, BEND.z - 0.5, BEND.y, BEND.y + 0.9, '#9aa0a6');
    // A cairn of the Way at the gorge's foot.
    const st = new THREE.DodecahedronGeometry(0.16, 0);
    for (let i = 0; i < 5; i++) kit.add(st, '#8d9298', place(-3.0, monLand(-3, 12.8) + 0.1 + i * 0.17, 12.8, i, 1 - i * 0.12), undefined, M_PLAIN);
    st.dispose();
  }

  /** North of the chapel: the pond's stony shore, the old dam with its sluice and great wheel (its own mesh: it turns), the aqueduct to the cistern. */
  private buildNorth(kit: Kit): void {
    // The dam: a long stone wall across the pond's east outlet, buttressed down into the ravine's head.
    const dz0 = DAM.z - DAM.len / 2;
    const dz1 = DAM.z + DAM.len / 2;
    this.box(kit, DAM.x - 0.5, DAM.x + 0.5, dz0, dz1, -2.5, 1.1, STONE[2] as string);
    this.box(kit, DAM.x - 0.6, DAM.x + 0.6, dz0 - 0.1, dz1 + 0.1, 1.1, 1.25, STONE[3] as string);
    for (let z = dz0 + 0.5; z < dz1; z += 1.2) this.box(kit, DAM.x + 0.5, DAM.x + 1.1, z - 0.25, z + 0.25, -4.5, 0.7, STONE_D);
    // The sluice: its stone frame and timber leaf; the wheel's posts and the screw to the leaf.
    this.box(kit, DAM.x - 0.62, DAM.x + 0.62, WHEEL.z - 0.75, WHEEL.z - 0.55, 0.9, 3.0, STONE[1] as string);
    this.box(kit, DAM.x - 0.62, DAM.x + 0.62, WHEEL.z + 0.55, WHEEL.z + 0.75, 0.9, 3.0, STONE[1] as string);
    this.box(kit, DAM.x - 0.62, DAM.x + 0.62, WHEEL.z - 0.75, WHEEL.z + 0.75, 3.0, 3.25, STONE[3] as string);
    this.box(kit, DAM.x - 0.08, DAM.x + 0.08, WHEEL.z - 0.5, WHEEL.z + 0.5, -0.8, 1.0, WOOD, M_PLAIN);
    // The wheel itself: a great timber wheel with spokes and handles, upright, face to the south.
    const wk = new Kit({ aMason: 0 });
    const rimT = new THREE.TorusGeometry(WHEEL.r, 0.08, 5, 24);
    wk.add(rimT, WOOD, new THREE.Matrix4(), undefined, M_PLAIN);
    rimT.dispose();
    const hub = new THREE.CylinderGeometry(0.16, 0.16, 0.3, 8).rotateX(Math.PI / 2);
    wk.add(hub, IRON, new THREE.Matrix4(), undefined, M_PLAIN);
    hub.dispose();
    const spoke = new THREE.BoxGeometry(0.07, WHEEL.r * 2, 0.07);
    for (let i = 0; i < 4; i++) wk.add(spoke, WOOD_D, new THREE.Matrix4().makeRotationZ((i * Math.PI) / 4), undefined, M_PLAIN);
    spoke.dispose();
    const handle = new THREE.CylinderGeometry(0.03, 0.03, 0.32, 5).rotateX(Math.PI / 2);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      wk.add(handle, WOOD, new THREE.Matrix4().makeTranslation(Math.cos(a) * (WHEEL.r + 0.12), Math.sin(a) * (WHEEL.r + 0.12), 0.08), undefined, M_PLAIN);
    }
    handle.dispose();
    const wm = stoneMat(this.su);
    this.mats.push(wm);
    const wheel = wk.build(wm, 'mon-wheel');
    if (wheel) {
      this.geos.push(wheel.geometry);
      wheel.position.set(WHEEL.x, WHEEL.y, WHEEL.z + 0.85);
      this.wheel = wheel;
      this.group.add(wheel);
    }
    this.box(kit, WHEEL.x - 0.1, WHEEL.x + 0.1, WHEEL.z + 0.7, WHEEL.z + 0.82, 1.0, WHEEL.y, WOOD_D, M_PLAIN);
    // The pond's shore: stones round it, flat ones to sit on at its south side.
    const rng = looksRng(19);
    const st = new THREE.DodecahedronGeometry(0.3, 0);
    for (let i = 0; i < (this.low ? 22 : 40); i++) {
      const a = rng() * Math.PI * 2;
      if (Math.cos(a) > 0.8 && Math.abs(Math.sin(a)) < 0.5) continue;
      const k = 0.98 + rng() * 0.12;
      const x = POND.x + Math.cos(a) * POND.rx * k;
      const z = POND.z + Math.sin(a) * POND.rz * k;
      const s = 0.35 + rng() * 0.5;
      kit.add(st, ['#4f5559', '#5b6165', '#464c50'][i % 3] as string, place(x, POND.y + 0.05, z, rng() * 6, new THREE.Vector3(s * 1.4, s * 0.55, s)), undefined, { aMason: 3 });
    }
    st.dispose();
  }


  /** Ferns and moss tufts at the cliffs' feet and in the gorge's cracks, ivy on the old walls' feet, a few small trees on the ledges. */
  private buildPlants(kit: Kit): void {
    const rng = looksRng(303);
    const frond = new THREE.ConeGeometry(0.05, 0.42, 3);
    frond.translate(0, 0.21, 0);
    const fern = (x: number, y: number, z: number, s: number): void => {
      const n = 6;
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + rng();
        const c = ['#3d5a2f', '#4b6a36', '#33502a', '#58763d'][i % 4] as string;
        kit.add(frond, c, place(x, y, z, a, new THREE.Vector3(s * 1.6, s * (0.9 + rng() * 0.4), s * 0.5), 1.05 + rng() * 0.3, 0), undefined, M_PLAIN);
      }
    };
    const N = this.low ? 40 : 90;
    for (let i = 0; i < N; i++) {
      // Along the gorge's cliff feet, the terrace's west and the pond.
      const pick = rng();
      let x: number;
      let z: number;
      if (pick < 0.6) {
        z = DOOR_Z + 0.4 + rng() * 14;
        x = (rng() < 0.5 ? -1 : 1) * (gorgeW(z) + 0.05 + rng() * 0.5);
      } else if (pick < 0.8) {
        z = -12 + rng() * 13;
        x = -10.3 - rng() * 1.5;
      } else {
        const a = rng() * Math.PI * 2;
        x = POND.x + Math.cos(a) * POND.rx * 1.06;
        z = POND.z + Math.sin(a) * POND.rz * 1.06;
      }
      fern(x, monLand(x, z), z, 0.7 + rng() * 0.6);
    }
    // Ivy and moss tufts at the walls' feet in the gorge (the south wall's outer face).
    const tuft = new THREE.SphereGeometry(0.16, 6, 4);
    for (let i = 0; i < (this.low ? 10 : 24); i++) {
      const x = -PH + rng() * 2 * PH;
      if (Math.abs(x) < 1.4) continue;
      kit.add(tuft, ['#3a5530', '#46633a'][i % 2] as string, place(x, 0.05 - (0.3) * 0, DOOR_Z + 0.08, 0, new THREE.Vector3(1.4 + rng(), 0.5 + rng() * 0.8, 0.5)), undefined, M_PLAIN);
    }
    tuft.dispose();
    // Small dark firs on the cliffs' ledges (high, beyond the board).
    const cone = new THREE.ConeGeometry(0.42, 1.3, 6);
    const trunk = new THREE.CylinderGeometry(0.05, 0.07, 0.4, 5);
    for (let i = 0; i < (this.low ? 22 : 48); i++) {
      const side = rng() < 0.5 ? -1 : 1;
      const z = -18 + rng() * 34;
      const x = side * (z > DOOR_Z ? gorgeW(z) + 2.2 + rng() * 6 : 11.5 + rng() * 6) + (side > 0 && z < 6 ? 3 : 0);
      const y = monLand(x, z);
      if (y < 2) continue;
      const s = 0.7 + rng() * 0.7;
      kit.add(trunk, '#2f2823', place(x, y + 0.2 * s, z, 0, s), undefined, M_PLAIN);
      kit.add(cone, ['#1f2e24', '#243528', '#1b2a20'][i % 3] as string, place(x, y + 0.95 * s, z, rng() * 3, new THREE.Vector3(s, s * (1 + rng() * 0.5), s)), undefined, M_PLAIN);
      kit.add(cone, '#23332a', place(x, y + 1.5 * s, z, rng() * 3, s * 0.7), undefined, M_PLAIN);
    }
    cone.dispose();
    trunk.dispose();
    frond.dispose();
  }

  /** The land: one heightfield (finer near the place), its colours by slope and wet, the rock's strata in the shader. */
  private buildLand(): void {
    const line = (a: number, b: number, fine0: number, fine1: number, fine: number, coarse: number): number[] => {
      const out: number[] = [];
      let v = a;
      while (v < b) {
        out.push(v);
        v += v >= fine0 && v < fine1 ? fine : coarse;
      }
      out.push(b);
      return out;
    };
    const f = this.low ? 0.5 : 0.32;
    const xs = line(-48, 48, -16, 18, f, 2.2);
    const zs = line(-100, 42, -27, 18, f, 2.6);
    const W = xs.length;
    const H = zs.length;
    const pos = new Float32Array(W * H * 3);
    const col = new Float32Array(W * H * 3);
    const c = new THREE.Color();
    const moss = new THREE.Color('#4a6339');
    const moss2 = new THREE.Color('#34472b');
    const rock = new THREE.Color('#545856');
    const rock2 = new THREE.Color('#3a3d3c');
    const pale = new THREE.Color('#787c7a');
    const ys = new Float32Array(W * H);
    for (let j = 0; j < H; j++)
      for (let i = 0; i < W; i++) {
        const x = xs[i] as number;
        const z = zs[j] as number;
        let y = farLand(x, z);
        // (Under the floor the land sits a hair below it.)
        if (floorSd(x, z) < 0.3 && Math.abs(x) < 11 && z > -13 && z < 15) y -= 0.03;
        ys[j * W + i] = y;
        pos[(j * W + i) * 3] = x;
        pos[(j * W + i) * 3 + 1] = y;
        pos[(j * W + i) * 3 + 2] = z;
      }
    // The cliffs' relief, from the smooth land's slope (the grid's own neighbours: no more land reads).
    const flat = ys.slice();
    for (let j = 1; j < H - 1; j++)
      for (let i = 1; i < W - 1; i++) {
        const k = j * W + i;
        const x = xs[i] as number;
        const z = zs[j] as number;
        const sx = ((flat[k + 1] as number) - (flat[k - 1] as number)) / ((xs[i + 1] as number) - (xs[i - 1] as number));
        const sz = ((flat[k + W] as number) - (flat[k - W] as number)) / ((zs[j + 1] as number) - (zs[j - 1] as number));
        const y = relief(x, z, flat[k] as number, Math.hypot(sx, sz));
        ys[k] = y;
        pos[k * 3 + 1] = y;
      }
    for (let j = 0; j < H; j++)
      for (let i = 0; i < W; i++) {
        const k = j * W + i;
        const x = xs[i] as number;
        const z = zs[j] as number;
        const yl = ys[j * W + Math.max(0, i - 1)] as number;
        const yr = ys[j * W + Math.min(W - 1, i + 1)] as number;
        const yd = ys[Math.max(0, j - 1) * W + i] as number;
        const yu = ys[Math.min(H - 1, j + 1) * W + i] as number;
        const dx = (xs[Math.min(W - 1, i + 1)] as number) - (xs[Math.max(0, i - 1)] as number);
        const dz = (zs[Math.min(H - 1, j + 1)] as number) - (zs[Math.max(0, j - 1)] as number);
        const slope = Math.hypot((yr - yl) / dx, (yu - yd) / dz);
        const n = fbm(x * 0.6, z * 0.6);
        // Moss where it lies flatter, bare wet rock on the faces, paler rock up high.
        const flat = 1 - smooth(0.6, 1.6, slope);
        c.copy(rock2).lerp(rock, n);
        c.lerp(pale, smooth(8, 22, ys[k] as number) * 0.5);
        c.lerp(n > 0.5 ? moss : moss2, flat * (0.55 + 0.4 * n));
        col[k * 3] = c.r;
        col[k * 3 + 1] = c.g;
        col[k * 3 + 2] = c.b;
      }
    const idx: number[] = [];
    for (let j = 0; j < H - 1; j++)
      for (let i = 0; i < W - 1; i++) {
        const a = j * W + i;
        const b = a + 1;
        const d = a + W;
        const e = d + 1;
        // (A hole over the cistern: its pit is the masonry's.)
        if (Math.hypot((xs[i] as number) + 0.0, (zs[j] as number) - CY) < CR + 0.45) continue;
        idx.push(a, d, b, b, d, e);
      }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(idx);
    g.computeVertexNormals();
    this.geos.push(g);
    const m = stoneMat(this.su, true);
    this.mats.push(m);
    const mesh = new THREE.Mesh(g, m);
    mesh.name = 'mon-land';
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  /** The floor: one sheet over the monastery and the gorge, on the land's heights; its shader paints the regions. */
  private buildFloor(): void {
    // (Down the gorge past the board's foot, so a phone's tall frame never sees the floor end.)
    const g = new THREE.PlaneGeometry(22, 38, this.low ? 44 : 88, this.low ? 70 : 140).rotateX(-Math.PI / 2);
    g.translate(0, 0, 6.2);
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setY(i, floorY(pos.getZ(i)) + 0.004);
    g.computeVertexNormals();
    this.geos.push(g);
    const m = floorMat(this.fu);
    this.mats.push(m);
    const mesh = new THREE.Mesh(g, m);
    mesh.name = 'mon-floor';
    this.group.add(mesh);
  }

  /** The pond, the cistern's water, the runnels from its gates and its spout, the aqueduct's water, the gorge's stream. */
  private buildWater(): void {
    // The pond.
    const pg = new THREE.CircleGeometry(1, 48).rotateX(-Math.PI / 2);
    pg.scale(POND.rx * 1.05, 1, POND.rz * 1.05);
    pg.translate(POND.x, POND.y, POND.z);
    this.geos.push(pg);
    const pm = waterMat('pond');
    this.pond = pm;
    this.waters.push(pm);
    this.mats.push(pm);
    const pond = new THREE.Mesh(pg, pm);
    pond.renderOrder = 2;
    pond.name = 'mon-pond';
    this.group.add(pond);
    // The cistern's water (its height at the marks).
    const cg = new THREE.CircleGeometry(CR - 0.01, 32).rotateX(-Math.PI / 2);
    this.geos.push(cg);
    const cm = waterMat('cistern');
    (cm.uniforms.fall as THREE.IUniform<THREE.Vector3>).value.set(this.spoutAt.x, 0, this.spoutAt.z);
    this.waters.push(cm);
    this.mats.push(cm);
    const cis = new THREE.Mesh(cg, cm);
    cis.position.set(0, BRIM, CY);
    cis.renderOrder = 2;
    cis.name = 'mon-cistern';
    this.group.add(cis);
    this.cistern = { mesh: cis, mat: cm, shown: -1 };
    // The runnels: one ribbon geometry for all the running water (the three gates', the spout's fall, the aqueduct, the
    // stream), each with its own flow in an attribute so one shader runs them; the gates' flows written per frame.
    const parts: { pts: THREE.Vector3[]; w: number; slot: number }[] = [];
    GATE_DIRS.forEach(([dx, dz], i) => {
      const a = new THREE.Vector3(dx * (CR + 0.2), 0.02, CY + dz * (CR + 0.2));
      const end = i === 1 ? new THREE.Vector3(0, 0.02, GN + 0.05) : new THREE.Vector3(dx * (AI - 0.05), 0.02, CY);
      parts.push({ pts: [a, end], w: 0.3, slot: i });
    });
    // The spout's water down into the cistern (upright), the aqueduct's channel, the gorge's stream.
    parts.push({ pts: [this.spoutAt.clone(), this.spoutAt.clone().setY(BRIM - 0.02).add(new THREE.Vector3(0.12, 0, 0.1))], w: 0.11, slot: 3 });
    const stream: THREE.Vector3[] = [];
    for (let z = DOOR_Z + 0.7; z < 17; z += 0.6) {
      const x = gorgeW(z) - 0.55 + Math.sin(z * 0.9) * 0.18;
      stream.push(new THREE.Vector3(x, floorY(z) + 0.02, z));
    }
    parts.push({ pts: stream, w: 0.42, slot: 3 });
    const geo = mergeRibbons(parts);
    this.geos.push(geo);
    const rm = waterMat('run');
    this.waters.push(rm);
    this.mats.push(rm);
    const runs = new THREE.Mesh(geo, rm);
    runs.renderOrder = 3;
    runs.name = 'mon-runs';
    this.group.add(runs);
    this.runGeo = geo;
    // The gates' leaves (raised when open).
    const leafG = new THREE.BoxGeometry(0.56, 0.42, 0.06);
    this.geos.push(leafG);
    const leafM = new THREE.MeshStandardMaterial({ color: '#3b3f46', roughness: 0.6, metalness: 0.35, flatShading: true });
    this.mats.push(leafM);
    GATE_DIRS.forEach(([dx, dz]) => {
      const leaf = new THREE.Mesh(leafG, leafM);
      leaf.position.set(dx * (CR + 0.25), 0.2, CY + dz * (CR + 0.25));
      leaf.rotation.y = Math.atan2(dx, dz);
      this.group.add(leaf);
      this.runnels.push({ mat: rm, flow: 0, gate: leaf, up: 0 });
    });
  }

  private runGeo: THREE.BufferGeometry | null = null;

  /** The waterfall: a tall sheet bowed out from the cirque's face down to the pond; the mist off its foot. */
  private buildFall(): void {
    const segX = 10;
    const segY = 24;
    const g = new THREE.PlaneGeometry(1, 1, segX, segY);
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const u = pos.getX(i) + 0.5;
      const v = pos.getY(i) + 0.5;
      const y = POND.y + (FALL.top - POND.y) * v;
      const w = FALL.w1 + (FALL.w0 - FALL.w1) * v;
      // Bowed out from the face: it leaves the lip, then hangs nearly straight; spreads at its foot.
      const out = 0.9 * Math.sqrt(1 - v) + 0.25 * Math.sin(u * Math.PI);
      pos.setXYZ(i, FALL.x + (u - 0.5) * w, y, FALL.z + 0.5 + out);
    }
    g.computeVertexNormals();
    this.geos.push(g);
    const m = fallMat();
    this.fall = m;
    this.mats.push(m);
    const mesh = new THREE.Mesh(g, m);
    mesh.renderOrder = 4;
    mesh.name = 'mon-fall';
    this.group.add(mesh);
    // The mist: three upright sheets crossing at the foot, one shader.
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 3; i++) {
      const p = new THREE.PlaneGeometry(9, 5.5).translate(0, 2.75, 0);
      p.rotateY((i - 1) * 0.7);
      p.translate(FALL.x, POND.y - 0.2, FALL.z + 1.8 + i * 0.4);
      parts.push(p.toNonIndexed());
      p.dispose();
    }
    const mg = mergeUv(parts);
    for (const p of parts) p.dispose();
    this.geos.push(mg);
    const mm = mistMat();
    this.mist = mm;
    this.mats.push(mm);
    const mist = new THREE.Mesh(mg, mm);
    mist.renderOrder = 6;
    mist.name = 'mon-mist';
    this.group.add(mist);
  }

  /** The flood's sheet (the low point): over the ravine, the monastery and the gorge. */
  private buildFlood(): void {
    const g = new THREE.PlaneGeometry(30, 70, 30, 140).rotateX(-Math.PI / 2);
    g.translate(2, 0, 10);
    const pos = g.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) pos.setY(i, Math.max(floorY(pos.getZ(i)), -1.2));
    this.geos.push(g);
    const m = floodMat();
    this.mats.push(m);
    const mesh = new THREE.Mesh(g, m);
    mesh.frustumCulled = false;
    mesh.renderOrder = 7;
    mesh.name = 'mon-flood';
    this.group.add(mesh);
    this.flood = { mesh, mat: m, t0: -1 };
    this.paint(this.hour);
  }

  /** The summit far up the Way: a crag with Somnhold's keep on it, dark against the sky; the Order's torches round it. */
  private buildSummit(): void {
    const k = new Kit({ aMason: 0 });
    const B = new THREE.BoxGeometry(1, 1, 1);
    const top = monSummitY() - 0.6;
    const at = (x: number, y: number, z: number, sx: number, sy: number, sz: number, c: string): void => k.add(B, c, place(SUMMIT.x + x * 1.8, top + y * 1.8, SUMMIT.z + z * 1.8, 0, new THREE.Vector3(sx * 1.8, sy * 1.8, sz * 1.8)), undefined, M_ASHLAR);
    // The crag under it, then the keep, its walls and towers.
    at(0, 3.2, 0, 3.2, 6.4, 3.2, '#8d939a');
    at(0, 6.7, 0, 3.6, 0.6, 3.6, '#9ca2a8');
    for (const [x, z] of [
      [-3.6, -2.4],
      [3.4, -2.6],
      [-3.4, 2.8],
      [3.6, 2.6],
    ] as const)
      at(x, 2, z, 1.4, 4, 1.4, '#7f858c');
    at(0, 1, -2.6, 7.6, 2, 0.6, '#7a8087');
    at(0, 1, 2.8, 7.6, 2, 0.6, '#7a8087');
    B.dispose();
    const mat = stoneMat(this.su);
    this.mats.push(mat);
    const mesh = k.build(mat, 'mon-summit');
    if (mesh) {
      this.geos.push(mesh.geometry);
      mesh.frustumCulled = false;
      mesh.scale.setScalar(1);
      this.summit = mesh;
      this.group.add(mesh);
    }
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      this.torchAt.push(new THREE.Vector3(SUMMIT.x + Math.cos(a) * 9.5, top + 1.0 + (i % 3) * 0.3, SUMMIT.z + Math.sin(a) * 9.5));
    }
  }

  private buildDecals(): void {
    const q = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.geos.push(q);
    const N = 160;
    const kind = new THREE.InstancedBufferAttribute(new Float32Array(N), 1);
    const alpha = new THREE.InstancedBufferAttribute(new Float32Array(N), 1);
    q.setAttribute('aKind', kind);
    q.setAttribute('aAlpha', alpha);
    const m = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -6,
      polygonOffsetUnits: -6,
      uniforms: { time: { value: 0 } },
      vertexShader: /* glsl */ `
        attribute float aKind;
        attribute float aAlpha;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        void main() {
          vKind = aKind; vAlpha = aAlpha; vUv = uv; vC = instanceColor;
          gl_Position = projectionMatrix * viewMatrix * modelMatrix * instanceMatrix * vec4(position, 1.0);
        }`,
      fragmentShader: /* glsl */ `
        uniform float time;
        varying float vKind;
        varying float vAlpha;
        varying vec2 vUv;
        varying vec3 vC;
        float h(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        void main() {
          vec2 p = vUv - 0.5;
          float r = length(p) * 2.0;
          float a = 0.0;
          vec3 c = vC;
          if (vKind < 0.5) {
            // Salt thrown: a ragged disc of grains, thicker at its middle, sparkling.
            float g = h(floor(vUv * 52.0));
            float edge = 1.0 - smoothstep(0.72 + 0.16 * g, 1.0, r);
            a = edge * (0.6 + 0.4 * g) * (0.75 + 0.25 * (1.0 - r));
            a += step(0.97, g) * edge * (0.5 + 0.5 * sin(time * 5.0 + g * 40.0));
          } else if (vKind < 1.5) {
            // A print in the salt: a narrow foot pressed dark, toes forward (+y of the quad), a heel.
            vec2 f = p * vec2(3.4, 1.7);
            float sole = 1.0 - smoothstep(0.33, 0.43, length(f - vec2(0.0, 0.08)));
            float heel = 1.0 - smoothstep(0.24, 0.32, length((p - vec2(0.0, -0.22)) * vec2(3.0, 2.6)));
            a = max(sole, heel);
          } else if (vKind < 2.5) {
            // A line of salt across a doorway: dense, ragged at its long edges.
            float g = h(floor(vUv * vec2(60.0, 10.0)));
            a = (1.0 - smoothstep(0.32 + 0.1 * g, 0.5, abs(p.y))) * smoothstep(0.5, 0.42, abs(p.x)) * (0.75 + 0.25 * g);
          } else if (vKind < 3.5) {
            float ring = 1.0 - smoothstep(0.0, 0.1, abs(r - 0.82));
            a = (ring + (1.0 - smoothstep(0.0, 0.82, r)) * 0.22) * (0.7 + 0.3 * sin(time * 9.0));
          } else {
            a = smoothstep(0.0, 0.5, 1.0 - abs(r - 0.8) * 5.0) * 0.9 + (1.0 - smoothstep(0.0, 0.8, r)) * 0.12;
          }
          gl_FragColor = vec4(c, clamp(a, 0.0, 1.0) * vAlpha);
          #include <colorspace_fragment>
        }`,
    });
    this.mats.push(m);
    const im = new THREE.InstancedMesh(q, m, N);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(N * 3), 3);
    im.count = 0;
    im.frustumCulled = false;
    im.renderOrder = 5;
    im.name = 'mon-decals';
    this.decals = im;
    this.group.add(im);
  }

  private buildAnchors(r: MonasteryRules | undefined): void {
    const C = r?.cells;
    const w = (x: number, y: number, h: number): THREE.Vector3 => new THREE.Vector3(x * K, monGround(x * K, y * K) + h, y * K);
    if (C) {
      this.anchors.spots = C.spots.map((p) => w(p.x, p.y, 0.06));
      // Where the hero stands to shut a door: the corridor's middle line before it.
      this.anchors.doors = C.doors.map((d) => w(d.x < 0 ? d.x + 500 : d.x - 500, d.y, 0.06));
      this.anchors.sack = w(C.sack.x, C.sack.y, 0.06);
    }
    if (r?.cloister) this.anchors.gates = r.cloister.gates.map((g) => w(g.x, g.y, 0.06));
    this.anchors.altar5 = new THREE.Vector3(0, 1.3, -8700 * K);
    this.anchors.altar6 = new THREE.Vector3(0, PIT + 0.9, CY);
    this.anchors.cistern = new THREE.Vector3(0, RIM + 0.4, CY);
    this.anchors.door = new THREE.Vector3(0, 0.06, DOOR_Z + 0.4);
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    if (s.tick === this.lastTick) return;
    this.lastTick = s.tick;
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.08);
      switch (e.kind) {
        case 'salt':
        case 'shut':
          this.ctx.vfx.burst(p, { n: 14, color: '#f2f5f7', speed: 0.05, up: 0.04, size: 0.06, life: 8, spread: 0.4 });
          this.ctx.vfx.ring(p, 0.1, 0.9, '#eef2f5', 6, 0.7);
          break;
        case 'fetch':
          this.ctx.vfx.burst(p, { n: 10, color: '#f2f5f7', speed: 0.03, up: 0.06, size: 0.05, life: 7 });
          break;
        case 'stuck':
          this.ctx.vfx.ring(p, 0.1, 0.6, '#ff8a6e', 5, 0.7);
          break;
        case 'fade':
          this.ctx.vfx.implode(this.ctx.at(e.x, e.y, 0.6), 0.6, { n: 10, color: '#cfd8e6', size: 0.08, life: 10, swirl: 1.2, lift: 0.02 });
          break;
        case 'altar':
          if (e.n === 1) {
            this.steam.push({ at: (this.altar5?.at ?? p).clone(), until: this.now + 2.5 });
            this.ctx.vfx.burst((this.altar5?.at ?? p).clone(), { n: 12, color: '#5a6068', speed: 0.02, up: 0.05, size: 0.12, life: 14, smoke: true });
          } else this.ctx.vfx.ring((this.altar5?.at ?? p).clone().setY(0.1), 0.2, 1.4, '#ffffff', 7, 0.7);
          break;
        case 'gate':
          this.ctx.vfx.burst(p, { n: 6, color: '#9aa2ac', speed: 0.04, up: 0.03, size: 0.05, life: 5 });
          break;
        case 'pour': {
          const d = GATE_DIRS[e.n ?? 0] as [number, number];
          const at = new THREE.Vector3(d[0] * (CR + 0.5), 0.12, CY + d[1] * (CR + 0.5));
          this.ctx.vfx.burst(at, { n: e.n === 2 ? 12 : 6, color: '#e6eef4', speed: 0.05, up: 0.06, size: 0.06, life: 7, spread: 0.3 });
          break;
        }
        case 'snuff':
          this.steam.push({ at: (this.altar5?.at ?? p).clone(), until: this.now + 3 });
          this.ctx.vfx.burst((this.altar5?.at ?? p).clone(), { n: 18, color: '#d7dee6', speed: 0.03, up: 0.07, size: 0.14, life: 16, smoke: true });
          this.ctx.shake(0.15);
          break;
        case 'rise':
          this.riseUntil = this.now + 2.2;
          this.ctx.shake(0.6);
          this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.2), { n: 22, color: '#5d6266', speed: 0.08, up: 0.1, size: 0.14, life: 14, gravity: 0.004 });
          this.ctx.vfx.ring(this.ctx.at(e.x, e.y, 0.08), 0.3, 2.4, '#ff8a6e', 8, 0.8);
          break;
        case 'lit':
          this.ctx.vfx.ring(new THREE.Vector3(0, RIM + 0.05, CY), 0.4, 2.6, '#ffffff', 9, 0.8);
          break;
        case 'reflect':
          this.buildReflection();
          break;
        case 'call':
        case 'cover':
          this.ctx.vfx.ring(p.setY(p.y - 0.04), 0.15, e.kind === 'cover' ? 1.1 : 0.7, '#eef2ff', 6, 0.6);
          break;
        case 'mark':
          break;
        default:
          break;
      }
    }
  }

  /**
   * The young squire in the water (the cloister): her miniature merged pale into one mesh, mirrored under the surface at
   * the pit's far side. Drawn over the water without the depth test (the pit's floor would cut her), so she stands where
   * every look at her passes through the surface.
   */
  private buildReflection(): void {
    if (this.reflection) return;
    const root = sendaModel('fled').root;
    root.updateMatrixWorld(true);
    const kit = new Kit();
    const tmp = new THREE.Color();
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      const mat = m.material as THREE.MeshStandardMaterial;
      tmp.set(mat?.color ?? '#cccccc');
      kit.add(m.geometry, tmp, m.matrixWorld);
    });
    const mat = reflectMat();
    this.mats.push(mat);
    const mesh = kit.build(mat, 'mon-reflection');
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) m.geometry?.dispose();
    });
    if (!mesh) return;
    this.geos.push(mesh.geometry);
    mesh.renderOrder = 3;
    mesh.frustumCulled = false;
    this.group.add(mesh);
    this.reflection = { mesh, mat, show: 0 };
  }

  private m4 = new THREE.Matrix4();
  private q4 = new THREE.Quaternion();
  private v3 = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private c3 = new THREE.Color();
  private yAxis = new THREE.Vector3(0, 1, 0);

  draw(s: BattleState): void {
    const r = s.rules?.senda?.monastery;
    const w = s.senda?.monastery;
    const K3 = r?.cloister;
    // The cistern at its marks (12: brimming; the cells and the memory: full and still).
    if (this.cistern) {
      const cap = K3?.cap ?? 12;
      const lv = K3 && w ? w.level : this.place === 'monasteryDawn' && !r ? 1 : cap - 1;
      const k = Math.max(0, Math.min(1, lv / cap));
      this.cistern.shown = this.cistern.shown < 0 ? k : this.cistern.shown + (k - this.cistern.shown) * 0.1;
      this.cistern.mesh.position.y = PIT + 0.02 + (BRIM - PIT - 0.02) * this.cistern.shown;
      this.cistern.mesh.visible = this.cistern.shown > 0.01;
    }
    // The gates: their leaves up when open, their water running.
    this.runnels.forEach((g, i) => {
      const open = !!w && !!K3 && w.open[i] === 1 && w.level > 0;
      g.flow += ((open ? 1 : 0) - g.flow) * 0.2;
      g.up += ((w && w.open[i] === 1 ? 1 : 0) - g.up) * 0.25;
      g.gate.position.y = 0.2 + g.up * 0.42;
    });
    this.writeFlows();
    // The altars: the fifth burns unless the spectres put it out; the sixth burns once lit.
    if (this.altar5) {
      let open = 1;
      if (r?.cells && w) open = w.outAt >= 0 ? 0 : 1;
      if (K3 && w) open = w.snuffs > 0 && this.steamOn() ? 0.15 : 1;
      this.altar5.open += (open - this.altar5.open) * 0.12;
    }
    if (this.altar6) {
      const lit = (K3 && w?.lit) || (this.place === 'monasteryDawn' && !r);
      const bare = K3 && w && w.level <= K3.bare;
      const open = lit ? 1 : bare && w ? Math.min(1, w.lightHeld / Math.max(1, K3.light)) * 0.7 : 0;
      this.altar6.open += (open - this.altar6.open) * 0.1;
    }
    // (Built from the state too: a fight joined or resynced after the event.)
    if (!this.reflection && K3 && w?.reflected) this.buildReflection();
    // The reflection: shown while the water is low enough to show her, under its surface.
    if (this.reflection && this.cistern) {
      const want = K3 && w && w.reflected && w.level <= K3.reflect + 2 && !w.lit ? 1 : 0;
      this.reflection.show += (want - this.reflection.show) * 0.04;
      const surf = this.cistern.mesh.position.y;
      const ref = this.reflection.mesh;
      // Mirrored about the surface: she stands on the far (north) step of the pit, looking down.
      ref.scale.set(0.95, -0.85, 0.95);
      ref.position.set(-0.45, surf - 0.01, CY - 0.4);
      ref.rotation.y = 0.3;
      ref.visible = this.reflection.show > 0.01;
      (this.reflection.mat.uniforms.show as THREE.IUniform<number>).value = this.reflection.show;
      (this.reflection.mat.uniforms.surf as THREE.IUniform<number>).value = surf;
    }
    this.drawDecals(s, r, w);
  }

  private steamOn(): boolean {
    return this.steam.some((x) => x.until > this.now);
  }

  /** The runnels' flows (the gates' as they open; the spout, the aqueduct, the stream always). */
  private flowSlot = [0, 0, 0, 1];
  private writeFlows(): void {
    const g = this.runGeo;
    if (!g) return;
    let changed = false;
    for (let i = 0; i < 3; i++) {
      const f = this.runnels[i]?.flow ?? 0;
      if (Math.abs(f - (this.flowSlot[i] as number)) > 0.01) {
        this.flowSlot[i] = f;
        changed = true;
      }
    }
    if (!changed) return;
    const slot = g.getAttribute('aSlot') as THREE.BufferAttribute;
    const flow = g.getAttribute('aFlow') as THREE.BufferAttribute;
    for (let i = 0; i < slot.count; i++) flow.setX(i, this.flowSlot[slot.getX(i)] as number);
    flow.needsUpdate = true;
  }

  private drawDecals(s: BattleState, r: MonasteryRules | undefined, w: MonasteryState | undefined): void {
    const d = this.decals;
    if (!d) return;
    const kind = d.geometry.getAttribute('aKind') as THREE.InstancedBufferAttribute;
    const alpha = d.geometry.getAttribute('aAlpha') as THREE.InstancedBufferAttribute;
    const cap = kind.count;
    let n = 0;
    const put = (x: number, z: number, y: number, sx: number, sz: number, yaw: number, k: number, a: number, col: THREE.Color): void => {
      if (n >= cap) return;
      this.q4.setFromAxisAngle(this.yAxis, yaw);
      this.m4.compose(this.v3.set(x, y, z), this.q4, this.s3.set(sx, 1, sz));
      d.setMatrixAt(n, this.m4);
      d.setColorAt(n, col);
      kind.setX(n, k);
      alpha.setX(n, a);
      n++;
    };
    const C = r?.cells;
    if (C && w) {
      const salt = this.c3.set('#f3f5f7');
      const dia = (C.radius * 2 * K) * 1.05;
      C.spots.forEach((p, i) => {
        if (!w.salted[i]) return;
        const age = s.tick - (w.salted[i] as number);
        const k = Math.min(1, age / 6);
        put(p.x * K, p.y * K, monGround(p.x * K, p.y * K) + 0.012, dia * (0.6 + 0.4 * k), dia * (0.6 + 0.4 * k), i * 1.7, D_SALT, 0.92, salt);
      });
      // The prints in the salt, pointing the way they went, fading.
      const dark = new THREE.Color('#3a3f47');
      const life = Math.max(1, C.prints);
      for (let i = 0; i + 4 < w.prints.length; i += 5) {
        const age = s.tick - (w.prints[i + 4] as number);
        if (age > life) continue;
        const x = (w.prints[i + 1] as number) * K;
        const z = (w.prints[i + 2] as number) * K;
        const yaw = facingToRotY(w.prints[i + 3] as number) + Math.PI;
        const fade = 1 - age / life;
        // Left and right, a stride apart (by the tick: they alternate).
        const side = ((w.prints[i + 4] as number) >> 2) & 1 ? 1 : -1;
        const ox = Math.cos(yaw) * 0.07 * side;
        const oz = -Math.sin(yaw) * 0.07 * side;
        put(x + ox, z + oz, 0.02, 0.17, 0.3, yaw, D_PRINT, 0.85 * fade, dark);
      }
      // A door shut: a white line of salt across it.
      if (w.shut >= 0) {
        const dr = C.doors[w.shut];
        if (dr) put(dr.x * K + (dr.x < 0 ? 0.22 : -0.22), dr.y * K, 0.016, 0.22, 0.95, 0, D_LINE, 1, salt);
      }
    }
    const K3 = r?.cloister;
    if (K3 && w) {
      // The great sentinel coming up: a red ring at the ledge as it rises; its way to the chapel while it walks.
      if (this.riseUntil > this.now) put(K3.boss.climb[0]! * K, K3.boss.climb[1]! * K, 0.03, 2.6, 2.6, 0, D_RING, 1, RED);
    }
    // The memory: a pale ring at Edda's side, where the squire is covered (a quiet mark, not a target).
    if (r?.porch && w && s.senda) {
      const e = s.senda.npcs.find((x) => x.id === 'edda');
      if (e) {
        const p = this.ctx.at(e.x, e.y, 0);
        put(p.x, p.z, p.y + 0.03, (r.porch.cover * 2 * K) * 0.98, (r.porch.cover * 2 * K) * 0.98, 0, D_SOFT, 0.22, this.c3.set('#e8eef8'));
      }
    }
    d.count = n;
    d.instanceMatrix.needsUpdate = true;
    if (d.instanceColor) d.instanceColor.needsUpdate = true;
    kind.needsUpdate = true;
    alpha.needsUpdate = true;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now > 0 ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    this.fu.uTime.value = now;
    this.su.uTime.value = now;
    for (const m of [this.fall, this.mist, this.decals?.material as THREE.ShaderMaterial | undefined, this.reflection?.mat, ...this.waters]) {
      const t = m?.uniforms?.time as THREE.IUniform<number> | undefined;
      if (t) t.value = now;
    }
    // The flood: the dam opens a few seconds in, the wheel spins alone, the front runs down to the gorge's foot and the
    // water rises over the monastery.
    if (this.flood) {
      if (this.flood.t0 < 0) this.flood.t0 = now;
      const t = now - this.flood.t0;
      const u = this.flood.mat.uniforms;
      const go = Math.max(0, t - 5);
      (u.front as THREE.IUniform<number>).value = -20 + go * 3.6;
      (u.level as THREE.IUniform<number>).value = Math.min(2.4, Math.max(0, go - 3) * 0.14);
      (u.time as THREE.IUniform<number>).value = now;
      if (this.fall) (this.fall.uniforms.flood as THREE.IUniform<number>).value = Math.min(1, go * 0.3);
      this.wheelSpin = Math.min(5, this.wheelSpin + dt * (t > 3 ? 1.6 : 0.4));
    } else this.wheelSpin = Math.max(0, this.wheelSpin - dt);
    if (this.wheel && this.wheelSpin > 0) this.wheel.rotation.z -= dt * this.wheelSpin * 0.5;
    this.glow.begin();
    const night = this.hour === 'monasteryNight' ? 1 : this.hour === 'monastery' ? 0.6 : 0.15;
    if (night > 0.2)
      this.lamps.forEach((l, i) => {
        const f = 0.85 + 0.15 * Math.sin(now * (3 + i * 0.7) + i);
        this.glow.add(l.x, l.y, l.z, FIRE, 0.7 + night * 0.6, (0.3 + 0.4 * night) * f);
      });
    // The chapel's candles, the hearth.
    this.candles.forEach((c, i) => {
      const f = 0.8 + 0.2 * Math.sin(now * (7 + i) + i * 2);
      this.glow.add(c.x, c.y, c.z, CANDLE, 0.35 + 0.25 * night, (0.5 + 0.3 * night) * f);
    });
    const hearthOn = this.hour === 'monasteryNight' || this.hour === 'monastery';
    if (hearthOn) this.glow.add(this.hearth.x, this.hearth.y, this.hearth.z, FIRE, 1.0, 0.4 * night);
    const fire = !this.scene && this.hour === 'monasteryNight' ? this.fireAt : null;
    if (fire) {
      const f = 0.8 + 0.2 * Math.sin(now * 9) * Math.sin(now * 5.3);
      this.glow.add(fire.x, fire.y + 0.2, fire.z, FIRE, 3.4, 0.6 * f);
      this.glow.add(fire.x, fire.y + 0.15, fire.z, new THREE.Color('#ffd9a8'), 0.9, 0.85 * f);
    }
    for (const [a, alt] of [
      [this.altar5, 5],
      [this.altar6, 6],
    ] as const) {
      if (!a) continue;
      const k = a.open;
      a.mat.emissive.copy(RED).lerp(WHITE, 0.55).multiplyScalar(k * 1.6);
      a.mat.color.set('#1c1d22').lerp(WHITE, k);
      if (k > 0.03) {
        const e = a.at;
        this.glow.add(e.x, e.y, e.z, RED_SOFT, 0.9 + 0.8 * k, 0.5 * k);
        this.glow.add(e.x, e.y, e.z, WHITE, 0.5 + 0.3 * k, 0.65 * k);
        if (alt === 6) this.glow.add(0, (this.cistern?.mesh.position.y ?? PIT) + 0.05, CY, RED_SOFT, 2.6, 0.2 * k);
      }
    }
    if (this.torchesOn)
      this.torchAt.forEach((t, i) => {
        const f = 0.75 + 0.25 * Math.sin(now * (5 + (i % 4)) + i);
        this.glow.add(t.x, t.y, t.z, TORCH, 4.2, 0.9 * f);
      });
    this.glow.end();
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** The cistern's three gates' directions from its middle (the orchards west, the kitchen north, the ravine east). */
const GATE_DIRS: [number, number][] = [
  [-1, 0],
  [0, -1],
  [1, 0],
];

/**
 * Ribbons through points (on their heights), each `w` wide, merged into one geometry: uv along (y, world units) and
 * across (x); `aSlot` says whose flow each vertex takes, `aFlow` holds it (written when it changes).
 */
const mergeRibbons = (parts: { pts: THREE.Vector3[]; w: number; slot: number }[]): THREE.BufferGeometry => {
  const pos: number[] = [];
  const uv: number[] = [];
  const slot: number[] = [];
  const flow: number[] = [];
  const idx: number[] = [];
  for (const part of parts) {
    const { pts, w } = part;
    const base = pos.length / 3;
    let len = 0;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i] as THREE.Vector3;
      const a = pts[Math.max(0, i - 1)] as THREE.Vector3;
      const b = pts[Math.min(pts.length - 1, i + 1)] as THREE.Vector3;
      let dx = b.x - a.x;
      let dz = b.z - a.z;
      const l = Math.hypot(dx, dz);
      // (An upright run, the spout's: across along x.)
      if (l < 1e-4) {
        dx = 0;
        dz = 1;
      } else {
        dx /= l;
        dz /= l;
      }
      const nx = -dz;
      const nz = dx;
      if (i > 0) len += p.distanceTo(pts[i - 1] as THREE.Vector3);
      pos.push(p.x + (nx * w) / 2, p.y, p.z + (nz * w) / 2, p.x - (nx * w) / 2, p.y, p.z - (nz * w) / 2);
      uv.push(0, len, 1, len);
      slot.push(part.slot, part.slot);
      flow.push(part.slot === 3 ? 1 : 0, part.slot === 3 ? 1 : 0);
      if (i > 0) {
        const k = base + i * 2;
        idx.push(k - 2, k - 1, k, k - 1, k + 1, k);
      }
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('aSlot', new THREE.Float32BufferAttribute(slot, 1));
  g.setAttribute('aFlow', new THREE.Float32BufferAttribute(flow, 1));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/** Merges non-indexed geometries with position and uv. */
const mergeUv = (parts: THREE.BufferGeometry[]): THREE.BufferGeometry => {
  let n = 0;
  for (const p of parts) n += (p.getAttribute('position') as THREE.BufferAttribute).count;
  const pos = new Float32Array(n * 3);
  const uv = new Float32Array(n * 2);
  let o = 0;
  for (const p of parts) {
    const a = p.getAttribute('position') as THREE.BufferAttribute;
    const u = p.getAttribute('uv') as THREE.BufferAttribute;
    pos.set(a.array as Float32Array, o * 3);
    uv.set(u.array as Float32Array, o * 2);
    o += a.count;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return g;
};
